import type { SupabaseClient } from '@supabase/supabase-js';
import { awaitJob } from '../../lib/await-job.js';
import { showToast, openModal, KF_LOGO_GOECOMMERCE, KF_LOGO_KAUFLAND } from '@vario-connect/frontend-design';
import { reconcileWithChannel, resolveDefaultSelection, warehouseSummary } from '../../lib/warehouse-selection.js';
import { renderOauthCardShell, initOauthCard } from './mcp-oauth-card.js';
import {
  ladeKategorieKatalog,
  ladeGewaehlteKategorien,
  ladeAlleSeiten,
  type KategorieKatalogEintrag,
} from '../../lib/category-path.js';
import {
  baueKategorieBaum,
  sichtbareBaumZeilen,
  anfangsOffeneKnoten,
  type KategorieBaumIndex,
} from '../../lib/category-tree.js';
import {
  baueAhnenZaehler,
  zaehlerAendern,
  istTeilweiseGewaehlt,
  ebenenweiseAufklappen,
  baueChips,
  elternPfad,
  hebeSuchbegriffHervor,
  vererbungsZiele,
  vererbungsMeldung,
  type AhnenZaehler,
  type VererbungsErgebnis,
} from '../../lib/category-modal.js';

// Statische Deko-SVGs aus dem Design-Handoff "Kaufland Einstellungen" (currentColor, stroke).
const SVG_TAGS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><path d="M7 7h.01"/></svg>';
const SVG_TRUCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 18V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h2"/><path d="M14 9h4l3 3v5a1 1 0 0 1-1 1h-1"/><circle cx="7.5" cy="18.5" r="1.8"/><circle cx="17.5" cy="18.5" r="1.8"/></svg>';
const SVG_CHECK_SM = '<svg class="ck" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6 9 17l-5-5"/></svg>';
const SVG_LOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';

// Storefront-Liste ist NICHT mehr hartcodiert — sie kommt katalog-getrieben vom Backend
// (kaufland-storefronts-available → supportedStorefronts, Quelle kaufland.storefronts).
// Neuer Marktplatz = 1 Katalog-Zeile → erscheint hier automatisch beim Abruf.
type StorefrontDef = { code: string; label: string };
// Fallback, falls das Backend (noch) kein supportedStorefronts liefert (alter Stand während Deploy).
const FALLBACK_STOREFRONTS: StorefrontDef[] = [
  { code: 'de', label: 'Deutschland' }, { code: 'cz', label: 'Tschechien' },
  { code: 'sk', label: 'Slowakei' },    { code: 'pl', label: 'Polen' },
  { code: 'at', label: 'Österreich' },  { code: 'fr', label: 'Frankreich' },
  { code: 'it', label: 'Italien' },
];
const CATEGORY_POLL_INTERVAL_MS = 10_000;

interface SettingsCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

interface StorefrontAddResponse {
  ok?: boolean;
  storefront?: string;
  label?: string;
  channelId?: string;
  alreadyPresent?: boolean;
  storefronts?: string[];
  error?: {
    code: string;
    message: string;
    probe?: { ok: boolean; status?: number; message?: string };
  };
}

interface StoredStorefront {
  storefront: string;
  vario_channel_id: string;
  vario_channel_label: string;
  vario_storage_id?: string | null;
  vario_storage_label?: string | null;
  vario_storage_ids?: string[] | null;
  suppress_stock_push?: boolean | null;
  auto_takeover_orders?: boolean | null;
  order_default_channel_fallback?: boolean | null;
  push_invoices?: boolean | null;
  push_shipments?: boolean | null;
  vario_payment_method_id?: string | null;
  vario_payment_method_label?: string | null;
  vario_payment_term_id?: string | null;
  vario_payment_term_label?: string | null;
  vario_delivery_method_id?: string | null;
  vario_delivery_method_label?: string | null;
  vario_delivery_term_id?: string | null;
  vario_delivery_term_label?: string | null;
}

interface StatusResponse {
  ok?: boolean;
  available?: string[];
  probes?: Array<{ storefront: string; ok: boolean; status?: number; message?: string }>;
  dbStorefronts?: StoredStorefront[];
  supportedStorefronts?: Array<{ code: string; name: string; currency: string }>;
  varioChannels?: Array<{ id: string; label: string }>;
  /** Je Storefront die Lager, die dem Vario-Kanal aktuell zugeordnet sind. DREIWERTIG:
   *  Array = echter Stand aus Vario, `null` = nicht lesbar, Schlüssel fehlt = kein Kanal.
   *  Nachgezogen wird die gespeicherte Auswahl NUR beim Array — bei `null` würde ein
   *  Vario-Aussetzer sonst die Bestandsquelle des Händlers leeren. */
  channelStorageIds?: Record<string, string[] | null>;
  varioError?: string;
  error?: { code: string; message: string };
}

interface VarioWarehouse {
  id: string;
  number: string | null;
  displayName: string | null;
  active: boolean | null;
  salesStorage: boolean | null;
}

interface PaymentOption {
  id: string;
  label: string;
}

// ----- Kaufland-Kategorien (Auswahl) ----------------------------------------
// Modal "Kategorien waehlen" (Task 5, 2026-08-09) in der jeweiligen Storefront-Kachel:
// der Haendler waehlt je Marktplatz aus Kauflands 6.087 Kategorien die eigenen aus. Nur
// fuer diese laedt der Connector Attributwerte vor (Migration 0043,
// RPCs kaufland_set_tenant_categories/kaufland_enqueue_category_refresh, beide mit
// Storefront als erstem Parameter).

/** Einzelner Suchtreffer im lokalen Katalog. */
interface KategorieTreffer {
  categoryId: number;
  /** Anzeigename der Kategorie SELBST. Die Fundstelle darin wird hervorgehoben, deshalb
   *  steht der Name getrennt vom Pfad und nicht als eine Zeile. */
  name: string;
  /** Brotkrumen der ELTERN, ueber parent_id selbst gebaut (siehe ladeKategorieKatalog fuer
   *  den Grund) und ohne den Sammelknoten an der Spitze. */
  elternPfad: string;
  istBlatt: boolean;
}

/** Zustand des Modals, lebt als Closure-Variable in openKategorieModal. */
interface KategorieAuswahlState {
  /** Kompletter Kategorie-Katalog GENAU DER Storefront, fuer die das Modal offen ist, einmal
   *  geladen. category_id -> Name + Elternkategorie. Kategorie-IDs sind laut Migration 0039
   *  storefrontuebergreifend identisch, die Namen aber je Sprache uebersetzt. */
  katalog: Map<number, KategorieKatalogEintrag>;
  /** category_id, die in keiner Zeile als parent_id auftaucht -- nur diese sind waehlbar. */
  blaetter: Set<number>;
  /** Aktuelle Suchtreffer, hoechstens KATEGORIE_TREFFER_LIMIT. */
  treffer: KategorieTreffer[];
  /** Wie viele Zeilen insgesamt zum Suchbegriff passen -- treffer.length kappt bei
   *  KATEGORIE_TREFFER_LIMIT, trefferGesamt nicht. Fix-Runde 1, Befund 3: ohne diese Zahl
   *  verschwinden bei kurzen Suchbegriffen (z. B. "e", ueber 5.000 Treffer) kommentarlos
   *  alle bis auf die ersten 50. */
  trefferGesamt: number;
  /** Aktueller Suchbegriff, getrimmt. Leer heisst: links steht der Baum, nicht die
   *  Trefferliste. Er steht im Zustand und nicht nur im Eingabefeld, weil die Trefferliste
   *  ihn zum Hervorheben der Fundstelle braucht. */
  suche: string;
  /** category_id -> Herkunft. 'auto' kam ueber einen EAN-Treffer oder Kauflands Vorschlag.
   *  Rein informativ (Tag "automatisch") -- Abwaehlbarkeit haengt NICHT davon ab, siehe
   *  speichernKategorien/urspruenglich. */
  gewaehlt: Map<number, 'manual' | 'auto'>;
  /** Elternknoten -> Anzahl gewaehlter Blaetter darunter. Traegt die Teil-Anzeige am
   *  Elternknoten und wird fortgeschrieben statt neu gerechnet -- siehe category-modal.ts. */
  teilZaehler: AhnenZaehler;
  /** Ob der Katalog genau EINEN Sammelknoten an der Spitze hat, dessen Name in jedem Pfad
   *  stuende, ohne etwas beizutragen. Steht im Zustand, weil Chips und Trefferliste
   *  denselben Pfad zeigen muessen. */
  sammelwurzelAusblenden: boolean;
  /** Schnappschuss der beim Laden vorgefundenen Auswahl (manual UND auto). Der Vergleich
   *  urspruenglich \ gewaehlt beim Speichern ergibt die Zeilen, die der Haendler abgehakt hat --
   *  siehe speichernKategorien fuer den Grund, warum das nicht ueber die RPC allein laeuft. */
  urspruenglich: Set<number>;
}

const KATEGORIE_TREFFER_LIMIT = 50;
// Die Filterung selbst laeuft rein lokal ueber den einmal geladenen Katalog -- es gibt also
// PRO Tastendruck keinen Request. Das kurze Debounce glaettet nur das Neuzeichnen der
// Trefferliste bei schnellem Tippen, gleiche Groessenordnung wie SEARCH_DEBOUNCE_MS im
// Vario-Artikel-Dialog (offers-article-dialog.ts).
const KATEGORIE_SEARCH_DEBOUNCE_MS = 200;

let cachedWarehouses: VarioWarehouse[] | null = null;
let cachedPaymentMethods: PaymentOption[] | null = null;
let cachedPaymentTerms: PaymentOption[] | null = null;
let cachedDeliveryMethods: PaymentOption[] | null = null;
let cachedDeliveryTerms: PaymentOption[] | null = null;

interface CategoryStatusRow {
  storefront: string;
  kaufland_count: number;
  vario_count: number;
  mapping_count: number;
  last_synced_at: string | null;
  run_status: string | null;
  run_phase: string | null;
  categories_fetched: number;
  categories_total: number;
  active_job_id: string | null;
}

interface ShippingGroupStatusRow {
  storefront: string;
  count: number;
  lastSyncedAt: string | null;
  /** Vollstaendige Liste fuer den Standard-Auswahl-Selector pro Card. */
  groups: Array<{ id_shipping_group: string; name: string | null }>;
  /** Aktuell als Standard markierte Versandgruppe (aus kaufland.offer_default_profiles,
   *  category_id IS NULL). null wenn noch kein Default gepflegt ist. */
  defaultShippingGroupId: string | null;
}

type StorefrontState = 'active' | 'channel-missing' | 'inactive-available' | 'not-authorised';

interface RowState {
  state: StorefrontState;
  probeMessage?: string | undefined;
  varioLabel?: string | undefined;
  storageId?: string | null | undefined;
  storageLabel?: string | null | undefined;
  storageIds?: string[] | undefined;
  suppressStockPush?: boolean | undefined;
  autoTakeoverOrders?: boolean | undefined;
  orderDefaultChannelFallback?: boolean | undefined;
  pushInvoices?: boolean | undefined;
  pushShipments?: boolean | undefined;
  paymentMethodId?: string | null | undefined;
  paymentMethodLabel?: string | null | undefined;
  paymentTermId?: string | null | undefined;
  paymentTermLabel?: string | null | undefined;
  deliveryMethodId?: string | null | undefined;
  deliveryMethodLabel?: string | null | undefined;
  deliveryTermId?: string | null | undefined;
  deliveryTermLabel?: string | null | undefined;
}

let categoryPollTimer: ReturnType<typeof setInterval> | null = null;

interface SettingsSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

/** Standalone-Slot-Renderer für den Vario-App-Tab `/#/settings`
 *  (system.apps.{appId}.tabs). Rendert die Marktplatz-Einstellungen mit eigenem
 *  Kopf (wie Log/Bestellungen) über die volle iframe-Höhe. */
/** Standalone-Slot-Renderer für den eigenen Vario-App-Tab `/#/mcp`
 *  (system.apps.{appId}.tabs, msgKey "MCP").
 *
 *  Warum ein eigener Tab und nicht nur die Karte unten in den Einstellungen: Der
 *  Einstellungen-Tab ist nach Storefronts gegliedert, der MCP-Zugang gehört zu keiner.
 *  Er lag dort am Ende unterhalb des Kachel-Rasters und war ohne Scrollen nicht zu sehen.
 *
 *  Die Karte selbst bleibt vorerst ZUSÄTZLICH in den Einstellungen stehen. Ein neuer Tab
 *  erscheint erst nach Re-Consent des Händlers (Integrations-Änderung im Manifest); bis
 *  dahin wäre der Zugang sonst gar nicht erreichbar. Das Entfernen dort ist ein Nachzug,
 *  sobald der Tab live bestätigt ist. */
export function renderMcpSlot(ctx: SettingsSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="kf-page" style="height:100%;display:flex;flex-direction:column;">
      <main class="main">
        <header class="apphead">
          <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
          <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
          <span class="hdivider"></span>
          <div class="htitle"><div class="heyebrow">Kaufland · MCP</div><h1>Zugang für KI-Assistenten</h1></div>
          <div class="hactions"><span class="hstatus"><span class="d"></span>Kaufland verbunden</span></div>
        </header>
        <div class="scrollarea">${renderOauthCardShell()}</div>
      </main>
    </div>`;

  // Volle iframe-Höhe ausnutzen, Scrolling innen — gleiche Mechanik wie Einstellungen
  // und Artikel-Zuordnung.
  const root = ctx.contentEl.querySelector<HTMLElement>('.kf-page')!;
  const fit = () => { root.style.height = `${window.innerHeight}px`; };
  window.addEventListener('resize', fit);
  window.addEventListener('orientationchange', fit);
  fit();

  // Die OAuth-Karte lebt in einem eigenen Modul: settings.ts ist bereits ueber
  // 1700 Zeilen lang, und der Zugang hat eine klar eigene Aufgabe.
  void initOauthCard(ctx.contentEl, {
    supabase: ctx.supabase,
    session: { token: ctx.session.token, tenantId: ctx.session.tenantId },
  });
}

export function renderSettings(ctx: SettingsSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = '';
  void renderSettingsTab(ctx.contentEl, { supabase: ctx.supabase, session: ctx.session, supabaseUrl: ctx.supabaseUrl }, { standalone: true });
}

export async function renderSettingsTab(host: HTMLElement, ctx: SettingsCtx, opts: { standalone?: boolean } = {}) {
  // Stop any leftover polling from a previous render (tab switch).
  stopCategoryPoll();

  const standalone = !!opts.standalone;
  const appheadHtml = standalone
    ? `<header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
        <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">Kaufland · Einstellungen</div><h1>Marktplatz-Einstellungen</h1></div>
        <div class="hactions">
          <span class="hstatus"><span class="d"></span>Kaufland verbunden</span>
          <button type="button" class="btn btn-primary" id="kl-settings-refresh" title="Marktplatz-Freischaltungen, Kategorien & Versandgruppen-Status neu von Kaufland/Vario laden">
            <span class="v-icon fa-regular fa-rotate-right" id="kl-settings-refresh-icon"></span>Aktualisieren
          </button>
        </div>
      </header>`
    : '';
  const grid = `<div id="kl-status-banner"></div><div id="kl-storefronts" class="mp-grid kl-storefront-grid">Prüfe Freischaltungen …</div>`;
  // Unter dem Storefront-Raster steht nichts mehr. Bis 2026-08-09 hingen hier zwei
  // Karten, beide sind aus demselben Grund verschwunden: Sie waren ohne Scrollen nicht
  // zu sehen, und beide gehoerten sachlich woandershin.
  //  - Der Zugang fuer KI-Assistenten lebt jetzt im eigenen Reiter "MCP"
  //    (renderMcpSlot). Er ist keine Storefront und gehoert deshalb weder ins Raster
  //    noch als Anhaengsel darunter.
  //  - Die globale Karte "Kaufland-Kategorien" ist entfallen: Die Auswahl galt
  //    storefront-uebergreifend, obwohl ein Haendler in Polen andere Kategorien bedient
  //    als in Deutschland. Sie sitzt jetzt als Modal in der jeweiligen Storefront-Kachel
  //    (siehe renderCategoryBlock/openKategorieModal weiter unten).
  // Beide Shell-Funktionen sind mit ihren Karten geloescht; wer hier wieder etwas anhaengt,
  // baut denselben unsichtbaren Platz erneut.
  host.innerHTML = standalone
    ? `<div class="kf-page" style="height:100%;display:flex;flex-direction:column;"><main class="main">${appheadHtml}<div class="scrollarea">${grid}</div></main></div>`
    : `<div class="kf-page" style="height:auto;display:block;">${grid}</div>`;

  if (standalone) {
    const root = host.querySelector<HTMLElement>('.kf-page')!;
    const fit = () => { root.style.height = window.innerHeight + 'px'; };
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    fit();

    // "Aktualisieren": lädt die komplette Erkennung neu — re-probt Kaufland
    // (neu freigeschaltete Marktplätze erscheinen), zieht Channel-/Kategorie-/
    // Versandgruppen-Status frisch. Re-Entry über renderSettingsTab; die fresh
    // gerenderte Liste zeigt "Prüfe Freischaltungen …" als Lade-Feedback.
    host.querySelector<HTMLButtonElement>('#kl-settings-refresh')?.addEventListener('click', () => {
      const icon = host.querySelector<HTMLElement>('#kl-settings-refresh-icon');
      if (icon) icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';
      void renderSettingsTab(host, ctx, opts);
    });
  }

  const listEl = host.querySelector<HTMLDivElement>('#kl-storefronts')!;
  const bannerEl = host.querySelector<HTMLDivElement>('#kl-status-banner')!;

  // Run channel-status (edge fn), category-status (RPC) and die Auswahl-Anzahl je Storefront
  // in parallel — alle drei speisen dieselbe UI.
  const statusPromise = fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/kaufland-storefronts-available`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
    body: '{}',
  }).then(async (res) => ({ res, body: (await res.json()) as StatusResponse }));
  const categoryStatusPromise = fetchCategoryStatus(ctx);
  const selectedCategoryCountsPromise = fetchSelectedCategoryCounts(ctx);

  let status: StatusResponse;
  try {
    const { res, body } = await statusPromise;
    status = body;
    if (!res.ok || !status.ok) {
      const msg = status.error?.message ?? `HTTP ${res.status}`;
      listEl.innerHTML = '';
      bannerEl.innerHTML = `<p class="install-status install-status--error">Status-Abruf fehlgeschlagen: ${escapeHtml(msg)}</p>`;
      if (status.error?.code === 'NO_INSTALL') {
        listEl.innerHTML = `<p>Noch keine Kaufland-Installation gefunden. Bitte die App über den Vario-Install-Slot installieren.</p>`;
      }
      return;
    }
  } catch (e) {
    listEl.innerHTML = `<p class="install-status install-status--error">Unerwarteter Fehler: ${escapeHtml((e as Error).message)}</p>`;
    return;
  }

  const dbByCode = new Map<string, StoredStorefront>();
  for (const s of status.dbStorefronts ?? []) dbByCode.set(s.storefront.toLowerCase(), s);

  const probeByCode = new Map<string, { ok: boolean; status?: number; message?: string }>();
  for (const p of status.probes ?? []) probeByCode.set(p.storefront, p);
  const varioChannelById = new Map<string, { id: string; label: string }>();
  for (const ch of status.varioChannels ?? []) varioChannelById.set(ch.id, ch);

  if (status.varioError) {
    bannerEl.innerHTML = `<p class="install-status install-status--info">Hinweis: Vario-Channel-Liste konnte nicht abgerufen werden (${escapeHtml(status.varioError)}). Anzeige der Channel-Gesundheit eingeschränkt.</p>`;
  }

  // Storefront-Liste katalog-getrieben aus der Backend-Antwort (Fallback: statische Liste,
  // falls eine ältere Backend-Version noch kein supportedStorefronts mitschickt).
  const COUNTRIES: StorefrontDef[] = (status.supportedStorefronts && status.supportedStorefronts.length)
    ? status.supportedStorefronts.map((s) => ({ code: s.code, label: s.name.replace(/^Kaufland\s+/i, '') }))
    : FALLBACK_STOREFRONTS;

  const states = new Map<string, RowState>();
  // Storefronts, deren Lagerauswahl beim Abgleich mit dem Vario-Kanal gekürzt wurde. Wird nach
  // dem Rendern persistiert und dem Händler gemeldet (siehe persistChannelReconcile weiter unten).
  const reconciled = new Map<string, { ids: string[]; defaultId: string | null; removed: string[] }>();
  for (const c of COUNTRIES) {
    const dbRow = dbByCode.get(c.code);
    const probe = probeByCode.get(c.code);
    const available = !!probe?.ok;
    const varioChannel = dbRow ? varioChannelById.get(dbRow.vario_channel_id) : undefined;

    let state: StorefrontState;
    if (dbRow && varioChannel) {
      state = 'active';
    } else if (dbRow && !varioChannel && !status.varioError) {
      state = 'channel-missing';
    } else if (dbRow) {
      state = 'active';
    } else if (available) {
      state = 'inactive-available';
    } else {
      state = 'not-authorised';
    }
    // Vario ist bei der Lager-Zuordnung führend: hat der Händler dort eine Zuordnung entfernt,
    // fliegt das Lager auch aus unserer Bestandsquelle. `channelStorageIds[code] === null` heißt
    // „nicht lesbar" und ändert bewusst nichts (sonst leert ein Vario-Aussetzer die Auswahl).
    const savedIds = dbRow?.vario_storage_ids ?? (dbRow?.vario_storage_id ? [dbRow.vario_storage_id] : []);
    const sync = reconcileWithChannel(
      savedIds,
      dbRow?.vario_storage_id ?? null,
      status.channelStorageIds?.[c.code],
    );
    if (sync.removed.length > 0) {
      reconciled.set(c.code, { ids: sync.ids, defaultId: sync.defaultId, removed: sync.removed });
    }

    states.set(c.code, {
      state,
      varioLabel: varioChannel?.label ?? dbRow?.vario_channel_label,
      probeMessage: probe && !probe.ok ? `${probe.status ?? ''} ${probe.message ?? ''}`.trim() : undefined,
      storageId: sync.defaultId ?? undefined,
      storageLabel: dbRow?.vario_storage_label,
      storageIds: sync.ids.length > 0 ? sync.ids : undefined,
      suppressStockPush: dbRow?.suppress_stock_push ?? false,
      autoTakeoverOrders: dbRow?.auto_takeover_orders ?? false,
      orderDefaultChannelFallback: dbRow?.order_default_channel_fallback ?? false,
      // Default false: Solange die Spalte fehlt oder nicht gesetzt ist, wird nichts gesendet.
      pushInvoices: dbRow?.push_invoices ?? false,
      pushShipments: dbRow?.push_shipments ?? false,
      paymentMethodId: dbRow?.vario_payment_method_id,
      paymentMethodLabel: dbRow?.vario_payment_method_label,
      paymentTermId: dbRow?.vario_payment_term_id,
      paymentTermLabel: dbRow?.vario_payment_term_label,
      deliveryMethodId: dbRow?.vario_delivery_method_id,
      deliveryMethodLabel: dbRow?.vario_delivery_method_label,
      deliveryTermId: dbRow?.vario_delivery_term_id,
      deliveryTermLabel: dbRow?.vario_delivery_term_label,
    });
  }

  // Vario-Lager-Liste vorab laden — best-effort, im Hintergrund. Render erfolgt ohne sie,
  // sobald die Liste da ist, füllen wir die Lager-Panels nach.
  const warehousesPromise = loadVarioWarehouses(ctx).catch((e) => {
    console.warn('[settings] vario-warehouses-list failed', e);
    return [] as VarioWarehouse[];
  });
  // Zahlart + Zahlungsbedingung + Versandart + Lieferbedingung in einem Call laden.
  const paymentPromise = loadVarioPaymentOptions(ctx).catch((e) => {
    console.warn('[settings] vario-payment-options-list failed', e);
    return {
      methods: [] as PaymentOption[],
      terms: [] as PaymentOption[],
      deliveryMethods: [] as PaymentOption[],
      deliveryTerms: [] as PaymentOption[],
    };
  });

  // Reihenfolge: zuerst die für den Händler freigeschalteten Marktplätze
  // (aktiv → channel-missing → inaktiv-aber-freigeschaltet), dann ganz unten
  // die nicht freigeschalteten. Innerhalb einer Gruppe bleibt die COUNTRIES-
  // Reihenfolge (de, cz, sk, pl, at, fr, it) erhalten.
  const STATE_ORDER: Record<StorefrontState, number> = {
    active: 0,
    'channel-missing': 1,
    'inactive-available': 2,
    'not-authorised': 3,
  };
  const sortedCountries = [...COUNTRIES].sort((a, b) => {
    const sa = STATE_ORDER[states.get(a.code)!.state];
    const sb = STATE_ORDER[states.get(b.code)!.state];
    if (sa !== sb) return sa - sb;
    return COUNTRIES.indexOf(a) - COUNTRIES.indexOf(b);
  });
  listEl.innerHTML = sortedCountries.map((c) => renderRow(c, states.get(c.code)!)).join('');

  // Die Artikel-Zuordnung (CSV-Export/-Import) saß hier kurzzeitig als Karte. Sie hat seit
  // 2026-08-02 einen eigenen App-Tab (`system.apps.{appId}.tabs` → /#/assign), damit sie in
  // Vario auffindbar ist. Bewusst nur EIN Einstiegspunkt, kein zweiter hier.

  // Sobald Lager-Liste da ist, alle Lager-Panels füllen — und erst dann den Kanal-Abgleich
  // persistieren, weil hier die Lagernamen für die Meldung auflösbar sind.
  void warehousesPromise.then((warehouses) => {
    cachedWarehouses = warehouses;
    listEl.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((wrap) => {
      applyWarehousesToMulti(wrap, warehouses);
    });
    for (const [code, sync] of reconciled) {
      void persistChannelReconcile(listEl, ctx, code, sync);
    }
  });
  // Sobald Zahlart-/Zahlungsbedingung-/Versandart-/Lieferbedingung-Listen
  // da sind, alle vier <select>-Familien füllen.
  void paymentPromise.then((opts) => {
    cachedPaymentMethods = opts.methods;
    cachedPaymentTerms = opts.terms;
    cachedDeliveryMethods = opts.deliveryMethods;
    cachedDeliveryTerms = opts.deliveryTerms;
    listEl.querySelectorAll<HTMLSelectElement>('select[data-payment-method-select]').forEach((sel) => {
      const currentId = sel.dataset.currentId ?? '';
      applyPaymentOptionsToSelect(sel, opts.methods, currentId);
    });
    listEl.querySelectorAll<HTMLSelectElement>('select[data-payment-term-select]').forEach((sel) => {
      const currentId = sel.dataset.currentId ?? '';
      applyPaymentOptionsToSelect(sel, opts.terms, currentId);
    });
    listEl.querySelectorAll<HTMLSelectElement>('select[data-delivery-method-select]').forEach((sel) => {
      const currentId = sel.dataset.currentId ?? '';
      applyPaymentOptionsToSelect(sel, opts.deliveryMethods, currentId);
    });
    listEl.querySelectorAll<HTMLSelectElement>('select[data-delivery-term-select]').forEach((sel) => {
      const currentId = sel.dataset.currentId ?? '';
      applyPaymentOptionsToSelect(sel, opts.deliveryTerms, currentId);
    });
  });

  // Aktivieren/Reparieren rendert den Tab danach KOMPLETT neu (derselbe Pfad wie der
  // „Aktualisieren"-Button). Vorher hat der Activate-Flow nur das Badge getauscht und zwei
  // leere Platzhalter angehängt — der ganze Einstellungs-Block (Vario-Lager, die beiden
  // Checkboxen, Zahlart/Zahlungsbedingung/Versandart/Lieferbedingung) fehlte bis zum
  // manuellen Refresh, und der „Aktivieren"-Button blieb stehen. Der Re-Render liest den
  // echten Zustand neu und trifft damit auch den Fall, dass die Aktivierung teilweise
  // durchlief (z. B. `channel-missing`).
  listEl.querySelectorAll<HTMLButtonElement>('button[data-action="activate"], button[data-action="repair"]').forEach((btn) => {
    btn.addEventListener('click', () => activateStorefront(btn, ctx, () => {
      void renderSettingsTab(host, ctx, opts);
    }));
  });
  // Lager: Panel auf/zu; gespeichert wird beim Zuklappen, wenn sich die Auswahl geaendert hat
  // (die uebrigen Selects der Karte speichern on-change — hier waere ein Call pro Klick).
  // Der Icon-Button daneben laedt die Vario-Lager-Liste neu.
  listEl.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((wrap) => {
    bindMultiInputs(wrap);
    const btn = wrap.querySelector<HTMLButtonElement>('[data-storage-toggle]');
    btn?.addEventListener('click', () => {
      const panel = wrap.querySelector<HTMLElement>('[data-storage-panel]');
      if (panel && panel.hidden) openStoragePanel(wrap, ctx);
      else void closeStoragePanel(wrap, ctx);
    });
  });
  bindStorageGlobalHandlers(ctx);
  listEl.querySelectorAll<HTMLButtonElement>('button[data-action="reload-warehouses"]').forEach((btn) => {
    btn.addEventListener('click', () => reloadWarehouses(btn, ctx));
  });
  listEl.querySelectorAll<HTMLInputElement>('input[data-suppress-stock]').forEach((cb) => {
    cb.addEventListener('change', () => toggleSuppressStock(cb, ctx));
  });
  listEl.querySelectorAll<HTMLInputElement>('input[data-auto-takeover]').forEach((cb) => {
    cb.addEventListener('change', () => toggleAutoTakeover(cb, ctx));
  });
  listEl.querySelectorAll<HTMLInputElement>('input[data-default-channel-fallback]').forEach((cb) => {
    cb.addEventListener('change', () => toggleDefaultChannelFallback(cb, ctx));
  });
  listEl.querySelectorAll<HTMLInputElement>('input[data-push-invoices]').forEach((cb) => {
    cb.addEventListener('change', () => togglePushFlag(cb, ctx, 'invoices'));
  });
  listEl.querySelectorAll<HTMLInputElement>('input[data-push-shipments]').forEach((cb) => {
    cb.addEventListener('change', () => togglePushFlag(cb, ctx, 'shipments'));
  });
  listEl.querySelectorAll<HTMLSelectElement>('select[data-payment-method-select]').forEach((sel) => {
    sel.addEventListener('change', () => savePaymentMethod(sel, ctx));
  });
  listEl.querySelectorAll<HTMLSelectElement>('select[data-payment-term-select]').forEach((sel) => {
    sel.addEventListener('change', () => savePaymentTerm(sel, ctx));
  });
  listEl.querySelectorAll<HTMLSelectElement>('select[data-delivery-method-select]').forEach((sel) => {
    sel.addEventListener('change', () => saveDeliveryMethod(sel, ctx));
  });
  listEl.querySelectorAll<HTMLSelectElement>('select[data-delivery-term-select]').forEach((sel) => {
    sel.addEventListener('change', () => saveDeliveryTerm(sel, ctx));
  });

  // Now wire the category-status pass — only the active rows actually have a placeholder for it.
  let categoryRows: CategoryStatusRow[] = [];
  let selectedCategoryCounts = new Map<string, number>();
  // Getrennt auswerten statt Promise.all: Die beiden Abrufe haengen fachlich nicht zusammen.
  // Mit Promise.all riss ein abgelehnter Zaehl-Abruf auch categoryRows leer, und jede Kachel
  // fiel auf "Kategorien werden gerade aus Kaufland geladen" zurueck, obwohl der Status-Abruf
  // geliefert hatte.
  const [statusErgebnis, zaehlErgebnis] = await Promise.allSettled([
    categoryStatusPromise,
    selectedCategoryCountsPromise,
  ]);
  if (statusErgebnis.status === 'fulfilled') categoryRows = statusErgebnis.value;
  else console.warn('[settings] category status fetch failed', statusErgebnis.reason);
  if (zaehlErgebnis.status === 'fulfilled') selectedCategoryCounts = zaehlErgebnis.value;
  else console.warn('[settings] selected category counts fetch failed', zaehlErgebnis.reason);
  applyCategoryStatus(listEl, categoryRows, selectedCategoryCounts, ctx);

  // If anything is currently syncing, start polling so the user sees live progress.
  if (categoryRows.some((r) => r.active_job_id || r.run_status === 'running')) {
    startCategoryPoll(listEl, ctx);
  }

  // Versandgruppen-Status separat laden — keine Polling-Mechanik, refresh ist
  // manuell und vergleichsweise schnell (1..2s pro Storefront).
  const shippingRows = await fetchShippingGroupStatus(ctx);
  applyShippingGroupStatus(listEl, shippingRows, ctx);
}


// ----- Kaufland-Kategorien (Auswahl) ----------------------------------------
// Auswahl je Storefront statt einer globalen Karte (Task 5, 2026-08-09): ein Haendler
// verkauft in Polen andere Kategorien als in Deutschland, die Vorgaenger-Karte in den
// Einstellungen (unabhaengig von jeder Storefront-Kachel) konnte das nicht abbilden.
// Die Auswahl sitzt deshalb jetzt als Modal in der jeweiligen Storefront-Kachel
// (renderCategoryBlock unten oeffnet openKategorieModal), Speichern ruft
// kaufland_set_tenant_categories MIT der Storefront. Der eigentliche Abruf der
// Attributwerte laeuft weiterhin im Hintergrund-Job kaufland.refresh_tenant_categories,
// von der RPC nach jeder Speicherung automatisch angestossen. KategorieTreffer,
// KategorieAuswahlState und die beiden Konstanten sind oben im Datei-Kopf definiert.

// Katalog-Cache PRO Storefront: Kategorie-IDs sind laut Migration 0039 storefrontuebergreifend
// identisch, die Namen aber je Sprache uebersetzt -- ein zweites Oeffnen desselben Marktplatzes
// laedt die 6.087 Zeilen deshalb kein zweites Mal. Ein Fehlversuch wird NICHT gecacht, sonst
// bliebe ein Modal nach einem kurzen Netzwerk-Aussetzer dauerhaft leer.
const kategorieKatalogCache = new Map<string, Promise<{ katalog: Map<number, KategorieKatalogEintrag>; blaetter: Set<number> }>>();

function ladeKategorieKatalogCached(
  supabase: SupabaseClient,
  storefront: string,
): Promise<{ katalog: Map<number, KategorieKatalogEintrag>; blaetter: Set<number> }> {
  let p = kategorieKatalogCache.get(storefront);
  if (!p) {
    p = ladeKategorieKatalog(supabase, storefront).then((result) => {
      // Fix-Runde 1, Befund 3: ein LEERES Ergebnis (Crawl laeuft noch oder ist fehlgeschlagen --
      // kaufland.refresh_categories stand live auf 14 fehlgeschlagenen Laeufen) darf nicht
      // dauerhaft gecacht bleiben, sonst liefert die Suche in diesem Tab fuer immer "Keine
      // Treffer", auch nachdem der Katalog nachtraeglich gefuellt wurde. Der aktuelle Aufruf
      // bekommt trotzdem das (leere) Ergebnis -- nur der naechste Aufruf versucht es erneut.
      if (result.katalog.size === 0) kategorieKatalogCache.delete(storefront);
      return result;
    });
    kategorieKatalogCache.set(storefront, p);
    p.catch(() => kategorieKatalogCache.delete(storefront));
  }
  return p;
}

// Ausgelagert, weil der Lister-Slot dasselbe braucht und der Haendler dort dieselbe
// Bezeichnung lesen soll:
//  - ../../lib/category-path.js: `kategorienPfad` (Brotkrumen ueber die parent_id-Kette,
//    weil `path` bei allen 6.087 Zeilen NULL ist) und `ladeKategorieKatalog` (kompletter
//    Katalog EINES Storefronts, geblaettert).
//  - ../../lib/category-tree.js: Aufbau des Baums und seine sichtbaren Zeilen.
//  - ../../lib/category-modal.js: Teil-Anzeige, Aufklapp-Aktion, Chips, Hervorhebung --
//    alles DOM-frei und getestet.

/** Rein lokale Filterung ueber den geladenen Katalog, kein Netzwerk-Aufruf. Sucht nur im
 *  Anzeigenamen (nicht im ganzen Brotkrumen-Pfad), analog zur `ilike`-Suche, die der
 *  Task-Brief ursprünglich gegen die DB vorsah. `gesamt` zaehlt ALLE Treffer mit, nicht nur
 *  die ins Ergebnis uebernommenen -- Fix-Runde 1, Befund 3: ohne die echte Gesamtzahl
 *  verschwinden bei kurzen Suchbegriffen kommentarlos alle Treffer jenseits von Platz 50.
 *  Der Elternpfad wird bewusst nur fuer die tatsaechlich angezeigten Treffer gebaut,
 *  nicht fuer alle -- bei einem Suchbegriff wie "e" waeren das Tausende unnoetige
 *  Elternketten-Aufloesungen. */
function sucheKategorien(
  katalog: Map<number, KategorieKatalogEintrag>,
  blaetter: Set<number>,
  suche: string,
  sammelwurzelAusblenden: boolean,
): { treffer: KategorieTreffer[]; gesamt: number } {
  const s = suche.trim().toLowerCase();
  if (s === '') return { treffer: [], gesamt: 0 };
  const treffer: KategorieTreffer[] = [];
  let gesamt = 0;
  for (const [id, eintrag] of katalog) {
    if (!eintrag.name.toLowerCase().includes(s)) continue;
    gesamt++;
    if (treffer.length < KATEGORIE_TREFFER_LIMIT) {
      treffer.push({
        categoryId: id,
        name: eintrag.name,
        elternPfad: elternPfad(katalog, id, sammelwurzelAusblenden),
        istBlatt: blaetter.has(id),
      });
    }
  }
  return { treffer, gesamt };
}

/** Einheitliche Leer-/Hinweisdarstellung ueber die GLOBAL gescopte .kl-search-empty-Klasse.
 *  Fix-Runde 1, Befund 2: `.mp-multi-empty` ist in kf-page.css strikt unter `.kf-page` gescopt
 *  (`.kf-page .mp-multi-empty{...}`) -- `openModal()` haengt sein Markup aber an
 *  `document.body`, ausserhalb von `.kf-page`. Dort griff die Regel nie, das Modal war komplett
 *  ungestylt. `.kl-search-empty` ist dieselbe Klasse, die `offers-article-dialog.ts` fuer seinen
 *  Leerzustand nutzt (dort nachweislich global, keine `.kf-page`-Abhaengigkeit). escapeHtml()
 *  sitzt HIER drin, nicht bei den Aufrufern -- so ist auch ein spaeter mit einer Fehlermeldung
 *  aufgerufener Fall automatisch maskiert. */
function renderKatEmpty(icon: string, text: string): string {
  return `<div class="kl-search-empty"><span class="v-icon fa-regular ${icon}"></span>${escapeHtml(text)}</div>`;
}

/** Statuszeile der Fussleiste. Als eigene Funktion, weil sie an drei Stellen entsteht
 *  (Laden, jede Auswahländerung, leerer Katalog) und dreimal denselben Satz bauen zu
 *  müssen genau die Art Abweichung ist, die niemandem auffällt. */
function kategorieStatusText(gewaehlt: number, katalogGroesse: number): string {
  if (katalogGroesse === 0) return `<strong>${gewaehlt}</strong> ausgewählt`;
  return `<strong>${gewaehlt.toLocaleString('de-DE')}</strong> von ${katalogGroesse.toLocaleString('de-DE')} Kategorien ausgewählt`;
}

/** Rechte Spalte: die gewählte Auswahl als Chips, Name oben und Elternpfad darunter.
 *
 *  Warum kein `.kl-search-result` wie links: Die Klasse trägt `cursor:pointer` und einen
 *  Hover-Rahmen, verspricht also, dass die ganze Zeile etwas tut. Hier tut nur der
 *  Entfernen-Knopf etwas.
 *
 *  Warum keine Checkbox mehr: In der Vorfassung war jeder Eintrag eine angehakte Checkbox,
 *  die man abhaken konnte. Ein Haken, der IMMER gesetzt ist und dessen einzige mögliche
 *  Bedienung das Entfernen ist, ist ein Knopf mit falschem Aussehen. Der Entwurf setzt
 *  stattdessen ein x je Zeile, das ist auch die Bedeutung.
 *
 *  Der Leerzustand ist absichtlich ein erklärender Text und keine leere Liste -- sonst
 *  wirkt das Modal bei einem frisch installierten Mandanten wie kaputt statt wie
 *  "noch nichts gewählt".
 *
 *  `auto` (Herkunft aus einem EAN-Treffer oder Kauflands Vorschlag) bleibt eine reine
 *  Herkunfts-Info ohne Sperre -- Fix-Runde 1, Befund 1: eine gesperrte Zeile hier und eine
 *  frei abwählbare in der Trefferliste war dieselbe Kategorie mit zwei Verhaltensweisen.
 *  Damit ein Abwählen auch wirklich etwas bewirkt, löscht speichernKategorien() abgewählte
 *  Zeilen gezielt aus `kaufland.tenant_categories`, bevor die RPC läuft -- siehe dort. */
function renderKategorieAusgewaehlt(state: KategorieAuswahlState): string {
  if (state.gewaehlt.size === 0) {
    return renderKatEmpty('fa-inbox', 'Noch keine Kategorie gewählt. Links im Baum anhaken oder oben suchen.');
  }
  return baueChips(state.katalog, state.gewaehlt.keys(), {
    sammelwurzelAusblenden: state.sammelwurzelAusblenden,
  })
    .map((chip) => {
      const auto = state.gewaehlt.get(chip.categoryId) === 'auto';
      const name = escapeHtml(chip.name);
      const pfad = escapeHtml(chip.pfad);
      return `<div class="kl-kat-chip">
        <div class="kl-kat-chip-body">
          <div class="kl-kat-chip-name" title="${name}">${name}${auto ? ' <span class="text-grey">· automatisch</span>' : ''}</div>
          ${chip.pfad ? `<div class="kl-kat-chip-path" title="${pfad}">${pfad}</div>` : ''}
        </div>
        <button type="button" class="kl-action-icon is-danger" data-kat-weg="${chip.categoryId}"
                title="Entfernen" aria-label="${name} aus der Auswahl entfernen">
          <span class="v-icon fa-regular fa-xmark"></span>
        </button>
      </div>`;
    })
    .join('');
}

/** Aktuelle Suchtreffer. Nur Blätter sind ankreuzbar -- eine Kategorie mit Unterkategorien
 *  würde beim Lister sonst keine sinnvolle Zuordnung ergeben. Herkunft (`manual`/`auto`)
 *  spielt hier bewusst KEINE Rolle für die Sperre (Fix-Runde 1, Befund 1).
 *  Übersteigt die Gesamtzahl der Treffer das Limit, erscheint darunter ein Hinweis mit der
 *  echten Trefferzahl, statt die überzähligen Treffer stillschweigend wegzulassen.
 *
 *  Die Fundstelle im Namen ist hervorgehoben (`<mark>`, siehe hebeSuchbegriffHervor).
 *  Diese Funktion maskiert selbst und gibt HTML zurück -- der Name darf hier deshalb NICHT
 *  ein zweites Mal durch escapeHtml. Der Pfad darunter schon, er geht roh durch.
 *
 *  `.is-disabled` (statt reinem `disabled`-Attribut auf dem Input) dimmt auch die ganze
 *  Zeile -- `.v-checkbox.is-disabled` setzt `pointer-events:none`, das `disabled`-Attribut
 *  bleibt zusätzlich als semantische Sperre auf dem Input selbst. */
function renderKategorieTreffer(state: KategorieAuswahlState): string {
  if (state.treffer.length === 0) {
    return renderKatEmpty(
      'fa-magnifying-glass',
      `Keine Kategorie zu „${state.suche}“ gefunden. Andere Schreibweise probieren.`,
    );
  }
  const zeilen = state.treffer
    .map((t) => {
      const checked = state.gewaehlt.has(t.categoryId);
      const gesperrtKlasse = t.istBlatt ? '' : ' is-disabled';
      const titel = t.istBlatt
        ? ''
        : ' title="Nur Blattkategorien sind wählbar. Diese Kategorie hat Unterkategorien."';
      const pfad = escapeHtml(t.elternPfad);
      return `<label class="kl-search-result v-checkbox${gesperrtKlasse}"${titel}>
        <input type="checkbox" data-kat-id="${t.categoryId}" data-kat-treffer
               ${checked ? 'checked' : ''}${t.istBlatt ? '' : ' disabled'} />
        <div class="kl-search-result-main">
          <div class="kl-search-result-title">${hebeSuchbegriffHervor(t.name, state.suche)}</div>
          ${t.elternPfad ? `<div class="kl-search-result-meta" title="${pfad}">${pfad}</div>` : ''}
        </div>
      </label>`;
    })
    .join('');
  if (state.trefferGesamt > state.treffer.length) {
    return `${zeilen}<div class="kl-form-hint">Zeigt ${state.treffer.length} von ${state.trefferGesamt} Treffern. Suchbegriff eingrenzen, um weitere zu sehen.</div>`;
  }
  return zeilen;
}

// Einrueckung je Baum-Ebene. `.kl-search-result` bringt Innenabstand mit, der Inline-Style
// ueberschreibt davon nur die linke Seite. Bewusst inline und keine neue CSS-Klasse: die
// Tiefe ist ein Datenwert und keine Gestaltungsvariante.
const KATEGORIE_BAUM_BASIS_PX = 10;
const KATEGORIE_BAUM_EINZUG_PX = 16;

/** Aufklappbarer Kategoriebaum, gerendert an DERSELBEN Stelle wie die Trefferliste
 *  (`#kl-kat-results`). Leeres Suchfeld zeigt den Baum, ein Suchbegriff die Treffer --
 *  kein Umschalter, keine Reiter, kein zweiter Bereich.
 *
 *  Zwei Zeilenarten, bewusst getrennt statt einer gemeinsamen:
 *   - Blatt: `<label class="kl-search-result v-checkbox">` mit Checkbox, exakt wie in
 *     renderKategorieTreffer. NUR Blätter sind wählbar, weil Kaufland Angebote nur in
 *     Blattkategorien zulässt.
 *   - Knoten mit Kindern: eigenes `<button>` zum Auf- und Zuklappen. Es steckt NICHT in
 *     einem `<label>` -- dort würde ein Klick zugleich eine Checkbox umschalten.
 *
 *  Der Knoten trägt eine Teil-Anzeige (`.kl-kat-part`), die zeigt, dass unter ihm etwas
 *  gewählt ist. Sie ist REINE ANZEIGE (Vorgabe des Betreibers): kein anklickbares
 *  Kästchen, kein Auswählen ganzer Äste. `pointer-events:none` hält den Klick beim
 *  umgebenden Aufklapp-Knopf, und weil sie für Screenreader ausgeblendet ist, steht ihre
 *  Aussage zusätzlich im `aria-label` des Knopfes.
 *
 *  Blattzeilen tragen einen unsichtbaren Pfeil-Platzhalter, damit Checkbox und Beschriftung
 *  über alle Zeilen einer Ebene in einer Flucht stehen.
 *
 *  Gerendert wird nur, was `sichtbareBaumZeilen` liefert, also die Wurzeln plus die
 *  aufgeklappten Äste. Der DE-Katalog hat 6.087 Kategorien; ein Voll-Render wäre im
 *  iframe spürbar träge -- siehe auch ebenenweiseAufklappen.
 *
 *  Alle verwendeten Klassen sind global definiert (bridge.css bzw. die eigene, eng
 *  gescopte styles/kategorie-modal.css), KEINE steht unter `.kf-page` -- openModal() hängt
 *  sein Markup an document.body, dort greift ein `.kf-page`-Scope nie. Genau daran war die
 *  erste Fassung dieses Modals gescheitert. */
function renderKategorieBaum(
  katalog: Map<number, KategorieKatalogEintrag>,
  index: KategorieBaumIndex,
  offen: ReadonlySet<number>,
  gewaehlt: Map<number, 'manual' | 'auto'>,
  teilZaehler: AhnenZaehler,
): string {
  const zeilen = sichtbareBaumZeilen(katalog, index, offen);
  if (zeilen.length === 0) {
    // Absicherung, kein regulaerer Leerzustand: zeigeBaum() laeuft nur bei nichtleerem
    // Katalog, und jeder nichtleere Katalog hat mindestens eine Wurzel (jede Kategorie ohne
    // gueltigen Elternteil wird zu einer). Der Zweig ist damit heute unerreichbar. Er bleibt
    // stehen, damit ein kuenftiger Aufrufer nicht auf eine leere Flaeche ohne Erklaerung
    // laeuft, und darf nicht als "der Leerzustand des Baums" gelesen werden.
    return renderKatEmpty('fa-folder-open', 'Der Katalog enthält keine Kategorien.');
  }
  return zeilen
    .map((z) => {
      // `box-sizing:border-box` gehoert hier ins Inline-Style und NICHT in `.kl-search-result`.
      // Die geteilte Klasse setzt `width:100%` plus `padding` plus `border`, aber kein
      // box-sizing (bridge.css). Ein `<button>` bringt border-box aus dem Browser-Stylesheet
      // mit, ein `<label>` nicht -- Blatt- und Knotenzeilen waeren damit unterschiedlich
      // breit, auf Ebene 8 um rund 174 Pixel, und der Container liefe waagerecht ueber. Die
      // Klasse selbst bleibt unangetastet: Trefferliste und offers-article-dialog.ts benutzen
      // sie mit, dort sind alle Zeilen gleich breit und der Fehler faellt nicht auf.
      const einzug = `box-sizing:border-box;padding-left:${KATEGORIE_BAUM_BASIS_PX + z.ebene * KATEGORIE_BAUM_EINZUG_PX}px;`;
      const name = escapeHtml(z.name);
      if (z.istBlatt) {
        return `<label class="kl-search-result v-checkbox" style="${einzug}">
          <span class="v-icon fa-regular fa-chevron-right kl-kat-chev is-leer" aria-hidden="true"></span>
          <input type="checkbox" data-kat-id="${z.categoryId}" data-kat-baum${gewaehlt.has(z.categoryId) ? ' checked' : ''} />
          <div class="kl-search-result-main">
            <div class="kl-search-result-title" title="${name}">${name}</div>
          </div>
        </label>`;
      }
      // Die Zahl der direkten Kinder steht nur am ZUGEKLAPPTEN Knoten: aufgeklappt stehen
      // die Kinder ohnehin darunter, dann waere die Zahl doppelte Information.
      //
      // Sie braucht eine Beschriftung, sonst liest ein Screenreader die Zeile als
      // "Alle Kategorien 13" vor. Der Knopf traegt deshalb ein `aria-label` mit Name, Anzahl
      // und -- falls zutreffend -- der Teil-Anzeige, die sichtbare Zahl zusaetzlich ein
      // `title`. Weitere Baum-Rollen (`role="tree"`, `role="group"`, Pfeiltasten-Navigation)
      // fehlen bewusst -- das waere ein eigener Umbau und keine Randnotiz dieser Aenderung.
      const kinderText = `${z.kinderAnzahl} Unterkategorie${z.kinderAnzahl === 1 ? '' : 'n'}`;
      const darunter = teilZaehler.get(z.categoryId) ?? 0;
      const teilText = darunter > 0 ? `, ${darunter} darunter ausgewählt` : '';
      const kinderZahl = z.offen
        ? ''
        : `<span class="text-grey" title="${kinderText}">${z.kinderAnzahl}</span>`;
      return `<button type="button" class="kl-search-result" data-kat-auf="${z.categoryId}"
              style="${einzug}" aria-expanded="${z.offen ? 'true' : 'false'}"
              aria-label="${name}, ${kinderText}${teilText}">
        <span class="v-icon fa-regular ${z.offen ? 'fa-chevron-down' : 'fa-chevron-right'} kl-kat-chev" aria-hidden="true"></span>
        <span class="kl-kat-part${darunter > 0 ? ' is-on' : ''}" data-kat-teil="${z.categoryId}" aria-hidden="true"></span>
        <div class="kl-search-result-main">
          <div class="kl-search-result-title" title="${name}">${name}</div>
        </div>
        ${kinderZahl}
      </button>`;
    })
    .join('');
}

async function speichernKategorien(
  ctx: SettingsCtx,
  storefront: string,
  state: KategorieAuswahlState,
  /** Zielkanäle der Vererbung. Leer = Haken nicht gesetzt oder kein zweiter Kanal vorhanden. */
  vererbeAn: string[],
  btn: HTMLButtonElement,
  showError: (msg: string) => void,
  onGespeichert: (jobs: number, vererbtText: string) => void,
): Promise<void> {
  const ids = [...state.gewaehlt.keys()];
  // Zeilen, die beim Laden noch da waren -- egal ob 'manual' oder 'auto' -- und jetzt nicht
  // mehr angehakt sind: der Haendler hat sie abgewaehlt. Fix-Runde 1, Befund 1:
  // `kaufland_set_tenant_categories` loescht bei `_added_by='manual'` NUR Zeilen mit
  // added_by='manual' (Migration 0039); eine abgewaehlte 'auto'-Zeile waere ueber die RPC
  // allein unveraendert stehengeblieben, obwohl "Gespeichert" gemeldet worden waere. Deshalb
  // hier ein gezieltes DELETE ueber BEIDE Herkuenfte, bevor die RPC mit dem verbleibenden Rest
  // laeuft (Weg A aus dem Coordinator-Feedback: auto-Kategorien ueberall wie normale Eintraege
  // behandeln, dafuer aber das Loeschen selbst uebernehmen).
  const entfernt = [...state.urspruenglich].filter((id) => !state.gewaehlt.has(id));
  const originalHtml = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Speichern …';
  try {
    if (entfernt.length > 0) {
      // storefront MUSS mit in den Filter: seit Migration 0043 ist der Primaerschluessel
      // (tenant_id, storefront, category_id) -- dieselbe category_id kann in mehreren
      // Storefronts gewaehlt sein, ein Loeschen nur nach category_id wuerde auch die Auswahl
      // fremder Storefronts treffen.
      const { error: delError } = await ctx.supabase
        .schema('kaufland')
        .from('tenant_categories')
        .delete()
        .eq('storefront', storefront)
        .in('category_id', entfernt);
      if (delError) throw new Error(delError.message);
    }

    const { data, error } = await ctx.supabase.rpc('kaufland_set_tenant_categories', {
      _storefront: storefront,
      _category_ids: ids,
      _added_by: 'manual',
    });
    if (error) throw new Error(error.message);
    const jobs = (data as { jobs?: string[] } | null)?.jobs ?? [];

    // Vererben läuft NACH dem eigenen Speichern und nur, wenn das geklappt hat -- sonst
    // stünde die Auswahl in fremden Kanälen, aber nicht in der eigenen. `_category_ids` ist
    // dieselbe Liste, die gerade gespeichert wurde.
    //
    // Die RPC ergänzt ausschließlich (Migration 0052): sie kennt kein DELETE, und Kanäle
    // ohne passenden Katalogeintrag -- `hr` führt keine Kategorien -- liefert sie als
    // `skipped` zurück, statt die ganze Operation zu werfen.
    //
    // Ein Fehlschlag HIER darf nicht als "Speichern fehlgeschlagen" erscheinen: die eigene
    // Auswahl liegt zu diesem Zeitpunkt bereits in der Datenbank. Der Fehler wird deshalb
    // nicht geworfen, sondern als Text mitgegeben -- die Meldung lautet dann "Kategorien
    // gespeichert. Vererben fehlgeschlagen: …", und das Modal schliesst, weil der
    // gespeicherte Zustand echt ist. Die Vererbung laesst sich einfach erneut ausloesen.
    let vererbtText = '';
    if (vererbeAn.length > 0) {
      btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Vererben …';
      try {
        const { data: vData, error: vError } = await ctx.supabase.rpc('kaufland_inherit_tenant_categories', {
          _from_storefront: storefront,
          _category_ids: ids,
        });
        if (vError) throw new Error(vError.message);
        vererbtText = vererbungsMeldung((vData ?? {}) as VererbungsErgebnis);
      } catch (ve) {
        vererbtText = `Vererben fehlgeschlagen: ${(ve as Error).message}`;
      }
    }

    onGespeichert(jobs.length, vererbtText);
  } catch (e) {
    showError(`Speichern fehlgeschlagen: ${(e as Error).message}`);
    btn.disabled = false;
    btn.innerHTML = originalHtml;
  }
}

// Breite der Modal-Karte, aus dem Design-Entwurf vom 2026-08-09 (dort 1020 x 660).
// Deutlich mehr als der 540er-Default von openModal, weil das Fenster jetzt zwei Spalten
// hat: links der Baum mit Einzug je Ebene (KATEGORIE_BAUM_EINZUG_PX = 16, auf Ebene 8 also
// 128 Pixel allein fuer die Einrueckung), rechts 340 Pixel Auswahl. openModal setzt daneben
// unveraendert `max-width:calc(100vw - 32px)`, auf schmalen Fenstern schrumpft die Karte
// also mit und laeuft nicht ueber; die rechte Spalte wird unter 900 Pixeln schmaler
// (styles/kategorie-modal.css).
const KATEGORIE_MODAL_BREITE_PX = 1020;

// Absoluter Hoehendeckel der Karte, zusaetzlich zum `calc(100vh - 48px)` aus app.css.
// openModal setzt daraus `max-height: min(calc(100vh - 48px), 660px)`, der 100vh-Deckel
// bleibt also erhalten und greift auf niedrigen Fenstern zuerst. Zusammen mit `align: 'top'`
// beginnt die Karte immer oben im Bild statt in der Mitte eines moeglicherweise viel zu
// hohen iframe.
//
// Warum ueberhaupt ein zweiter Deckel: `100vh` ist im Vario-iframe die Hoehe des IFRAME,
// nicht die des sichtbaren Hostfensters. Ist das iframe hoeher als der sichtbare Bereich,
// deckelt `100vh` auf einen Wert, den der Host nie zeigt. Erreichbar ist der untere Teil
// dann mit keiner Geste: Vario clippt den iframe-Container ohne Scrollbalken (main.ts),
// openModal sperrt zusaetzlich den Body-Scroll, und ein `position: fixed`-Overlay wandert
// ohnehin nicht mit.
//
// 660 IST EINE VORGABE DES BETREIBERS (2026-08-09) und keine hergeleitete Zahl. Der Wert
// stand am selben Abend schon einmal auf 560, nachdem der Betreiber "unten abgeschnitten,
// nichts scrollt" gemeldet hatte; er hat sich danach ausdruecklich fuer die 660 aus dem
// Design-Entwurf entschieden. Der bekannte Preis: Zusammen mit den 24 Pixeln Innenabstand
// von `.kl-modal-wrap` endet die Karte bei 684. IST DER SICHTBARE BEREICH DES HOSTFENSTERS
// NIEDRIGER ALS 684 PIXEL, IST DIE FUSSLEISTE MIT DEM SPEICHERN-KNOPF WIEDER ABGESCHNITTEN
// -- genau der Zustand, den die 560 beseitigt hatten. DIESE ZAHL IST DER KNOPF DAFUER: Wer
// die Meldung erneut bekommt, senkt sie und aendert sonst nichts.
//
// Ein verlaessliches Mass fuer die sichtbare Hoehe des Hostfensters gibt es aus dem iframe
// heraus nicht: `window.innerHeight` ist die iframe-Hoehe, und `window.screen` beschreibt
// den Bildschirm, nicht das Fenster. Solange das so ist, bleibt es ein Festwert.
const KATEGORIE_MODAL_MAX_HOEHE_PX = 660;

// Ruecklaufzeit der zweiten Stufe von "Alle entfernen". Kein Browser-Dialog (Projektvorgabe),
// stattdessen ein zweiter Klick; bleibt er aus, faellt der Knopf in seinen Ausgangszustand
// zurueck, damit ein spaeterer Klick nicht ueberraschend loescht.
const KATEGORIE_ENTFERNEN_FRIST_MS = 4000;

/** Markup des Modal-Inhalts nach dem Design-Entwurf vom 2026-08-09: Suchzeile mit
 *  Hinweis-Knopf, darunter zwei Spalten (links Baum oder Trefferliste, rechts die Auswahl),
 *  darunter die Meldungszeile. Ohne Katalog/Auswahl -- openKategorieModal() laedt nach und
 *  schaltet erst dann Suche und Speichern frei; vorher waere die Suche auf einem leeren
 *  Katalog aktiv und wuerde nichts finden.
 *
 *  Die Vertikale regelt jetzt CSS (styles/kategorie-modal.css) statt einer Reihe von
 *  Inline-Styles: `.kl-kat-panes` ist ein Grid mit fester rechter Spalte, beide Spalten
 *  scrollen INNEN. Vorher addierten sich feste Pixelhoehen aus bridge.css und Inline-Werten,
 *  und die Karte kam unabhaengig vom vorhandenen Platz auf ihre Hoehe. */
function renderKategorieModalBody(): string {
  return `
    <div class="kl-kat-modal">
      <div class="kl-kat-searchrow">
        <div class="v-input kl-search-input">
          <span class="v-icon fa-regular fa-magnifying-glass"></span>
          <input id="kl-kat-search" type="search" placeholder="Kategorie suchen, z. B. Schrauben …"
                 autocomplete="off" aria-label="Kategorie suchen" disabled />
          <button type="button" id="kl-kat-search-clear" class="kl-search-clear" aria-label="Suche zurücksetzen" hidden>
            <span class="v-icon fa-regular fa-xmark"></span>
          </button>
        </div>
        <button type="button" class="kl-action-icon kl-kat-hintbtn" id="kl-kat-hint"
                aria-expanded="false" aria-controls="kl-kat-hintpop" title="Wozu dient die Auswahl?"
                aria-label="Wozu dient die Auswahl?">
          <span class="v-icon fa-regular fa-circle-question"></span>
        </button>
        <div class="kl-kat-hintpop" id="kl-kat-hintpop" role="note" hidden>
          Wählen Sie die Kategorien, in denen Sie auf diesem Marktplatz verkaufen. Nur für diese
          lädt der Connector Kauflands vorgegebene <strong>Auswahlwerte</strong> vor, und der
          Lister zeigt später eine <strong>kurze Liste</strong> statt einer Suche über den
          ganzen Katalog.
        </div>
      </div>

      <div class="kl-kat-panes">
        <section class="kl-kat-pane kl-kat-pane--left">
          <header class="kl-kat-panehead">
            <span class="kl-kat-panelabel" id="kl-kat-left-label">Kategoriebaum</span>
            <span class="v-pill v-pill--muted" id="kl-kat-left-count" hidden></span>
            <span class="kl-kat-spacer"></span>
            <button type="button" class="kl-kat-link" id="kl-kat-expand" disabled>Alle aufklappen</button>
            <button type="button" class="kl-kat-link" id="kl-kat-collapse" disabled>Zuklappen</button>
          </header>
          <!-- Startzustand ist der Ladehinweis, NICHT der Baum: der Katalog ist hier noch nicht
               da. openKategorieModal() ersetzt ihn nach dem Laden durch den Baum (oder bei
               leerem Katalog durch den gesperrten Hinweis). -->
          <div class="kl-search-results kl-kat-scroll" id="kl-kat-results">
            ${renderKatEmpty('fa-spinner-third fa-spin', 'Lade Kategorien …')}
          </div>
        </section>

        <section class="kl-kat-pane">
          <header class="kl-kat-panehead">
            <span class="kl-kat-panelabel">Ausgewählt</span>
            <span class="v-pill v-pill--muted" id="kl-kat-sel-count">0</span>
            <span class="kl-kat-spacer"></span>
            <button type="button" class="kl-kat-link" id="kl-kat-clear-sel" disabled>Alle entfernen</button>
          </header>
          <div class="kl-search-results kl-kat-scroll" id="kl-kat-selected">
            ${renderKatEmpty('fa-spinner-third fa-spin', 'Lade Kategorien …')}
          </div>
        </section>
      </div>

      <div class="kl-kat-meldungen">
        <div class="v-banner v-banner-info" id="kl-kat-hinweis" hidden></div>
        <div class="kl-modal-error" id="kl-kat-status" hidden></div>
      </div>
    </div>
  `;
}

/** Oeffnet das Kategorie-Modal fuer GENAU eine Storefront. Uebernimmt aus der frueheren
 *  globalen Karte: einmaliges Laden des Katalogs (hier: ueber ladeKategorieKatalogCached,
 *  PRO Storefront gecacht), Blatt-Erkennung, den Hinweis bei mehr als 50 Treffern, und das
 *  gezielte Loeschen abgewaehlter Zeilen in speichernKategorien(). Speichern schliesst das
 *  Modal und ruft onSaved(), damit die aufrufende Kachel neu gezeichnet wird und der Push
 *  nach Vario anlaeuft. */
function openKategorieModal(
  ctx: SettingsCtx,
  storefront: string,
  label: string,
  vererbungsKanaele: string[],
  onSaved: () => void,
): void {
  // Vor openModal deklariert, damit onClose die laufenden Timer noch erreicht:
  // ModalOptions.onClose (packages/frontend-design/src/modal.ts) laeuft NACH dem
  // root.remove(), ein dann noch offener setTimeout liefe gegen entfernte Knoten.
  let debounceTimer: number | null = null;
  let entfernenTimer: number | null = null;

  const modal = openModal({
    // openModal interpoliert den Titel ROH ins Markup -- `label` ist deshalb hier maskiert.
    // Der zweizeilige Kopf (Titel oben, Marktplatz und Katalogumfang darunter) kommt aus dem
    // Entwurf. openModal kennt kein Untertitel-Feld, und eines dafuer einzufuehren haette
    // jedes andere Modal dieser App angefasst -- die Zeilen stecken deshalb im Titel selbst.
    title: `<span class="kl-kat-title">
        <span class="kl-kat-title-main">Kategorien wählen</span>
        <span class="kl-kat-title-sub" id="kl-kat-subtitle">Kaufland ${escapeHtml(label)}</span>
      </span>`,
    icon: 'fa-list-check',
    width: KATEGORIE_MODAL_BREITE_PX,
    // Oben verankert statt mittig. Im Vario-iframe kann die Mitte von `100vh` unterhalb der
    // sichtbaren Kante des Hostfensters liegen, und nichts davon ist erreichbar -- Vario clippt
    // den Container ohne Scrollbalken, openModal sperrt zusaetzlich den Body-Scroll. Oben
    // verankert beginnt die Karte immer im Bild.
    align: 'top',
    maxHeight: KATEGORIE_MODAL_MAX_HOEHE_PX,
    body: renderKategorieModalBody(),
    // Das Vererben-Kaestchen steht VOR der Statuszeile, also am linken Rand der Fussleiste,
    // und wird nur gerendert, wenn es ueberhaupt einen zweiten aktiven Kanal gibt. Es ist
    // bewusst NICHT vorbelegt und merkt sich seinen Zustand nicht ueber das Schliessen
    // hinaus: eine Aktion, die mehrere Kanaele auf einmal schreibt, darf nicht aus einem
    // Haken von vorgestern heraus passieren, den man beim Speichern gar nicht mehr ansieht.
    footer: `
      ${
        vererbungsKanaele.length > 0
          ? `<label class="kl-kat-vererben" title="Ergänzt die Auswahl der anderen Kanäle. Dort bereits gewählte Kategorien bleiben erhalten.">
               <input type="checkbox" id="kl-kat-vererben">
               <span>Auch auf andere aktive Kanäle vererben (${vererbungsKanaele.length})</span>
             </label>`
          : ''
      }
      <span class="kl-kat-footstatus" id="kl-kat-footstatus"></span>
      <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
      <button type="button" class="v-button v-button--save" id="kl-kat-save" disabled>
        <span class="v-icon fa-regular fa-check"></span> Speichern
      </button>
    `,
    onClose: () => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      debounceTimer = null;
      if (entfernenTimer !== null) window.clearTimeout(entfernenTimer);
      entfernenTimer = null;
      // Der Listener haengt an `document` und ueberlebt das Entfernen des Modals sonst.
      document.removeEventListener('click', hinweisKlickDraussen);
    },
  });

  // Kopf und Koerper brauchen eigene Regeln (Icon-Kachel, randloser Koerper). openModal
  // vergibt dafuer keine Klasse, und ein Parameter dafuer haette dessen Schnittstelle fuer
  // alle Modals dieser App erweitert. Beides steht in styles/kategorie-modal.css.
  modal.el.querySelector<HTMLElement>('.kl-modal-head')?.classList.add('kl-kat-head');
  modal.el.querySelector<HTMLElement>('.kl-modal-body')?.classList.add('kl-kat-body');

  const q = <T extends HTMLElement>(sel: string): T => modal.el.querySelector<T>(sel)!;
  const subtitleEl = q<HTMLElement>('#kl-kat-subtitle');
  const selectedEl = q<HTMLDivElement>('#kl-kat-selected');
  const resultsEl = q<HTMLDivElement>('#kl-kat-results');
  const searchInput = q<HTMLInputElement>('#kl-kat-search');
  const clearBtn = q<HTMLButtonElement>('#kl-kat-search-clear');
  const saveBtn = q<HTMLButtonElement>('#kl-kat-save');
  const errorEl = q<HTMLDivElement>('#kl-kat-status');
  const hinweisEl = q<HTMLDivElement>('#kl-kat-hinweis');
  const linkeUeberschrift = q<HTMLElement>('#kl-kat-left-label');
  const linkeZahl = q<HTMLElement>('#kl-kat-left-count');
  const auswahlZahl = q<HTMLElement>('#kl-kat-sel-count');
  const aufklappBtn = q<HTMLButtonElement>('#kl-kat-expand');
  const zuklappBtn = q<HTMLButtonElement>('#kl-kat-collapse');
  const alleWegBtn = q<HTMLButtonElement>('#kl-kat-clear-sel');
  const hinweisBtn = q<HTMLButtonElement>('#kl-kat-hint');
  const hinweisPop = q<HTMLDivElement>('#kl-kat-hintpop');
  const fussStatus = q<HTMLElement>('#kl-kat-footstatus');

  // Fix-Runde 1, Befund 2: dasselbe hidden-Attribut-Muster wie in offers-article-dialog.ts
  // (`.kl-modal-error`, global gescopt) statt eines ok/err-Klassenwechsels auf `.mp-msg`, der
  // ausserhalb `.kf-page` keine Farbe hatte. Ein Erfolg braucht keine eigene Anzeige -- das
  // Modal schliesst dabei sofort, siehe onGespeichert weiter unten.
  const showError = (msg: string) => {
    errorEl.textContent = msg;
    errorEl.hidden = false;
  };
  const clearError = () => {
    errorEl.textContent = '';
    errorEl.hidden = true;
  };
  // Getrennt vom Fehler und in anderer Farbe (`v-banner-info`): Der gedeckelte Aufklapp-Knopf
  // meldet hier eine Einschraenkung, keinen Fehlschlag. Beides rot zu zeigen liesse den
  // Haendler nach einem Problem suchen, das es nicht gibt.
  const zeigeHinweis = (msg: string) => {
    hinweisEl.textContent = msg;
    hinweisEl.hidden = false;
  };
  const hinweisWeg = () => {
    hinweisEl.textContent = '';
    hinweisEl.hidden = true;
  };

  // ----- Erklaertext als Popover -------------------------------------------
  const hinweisPopSchliessen = () => {
    hinweisPop.hidden = true;
    hinweisBtn.setAttribute('aria-expanded', 'false');
  };
  const hinweisKlickDraussen = (e: MouseEvent) => {
    if (hinweisPop.hidden) return;
    const ziel = e.target as Node | null;
    if (ziel && (hinweisBtn.contains(ziel) || hinweisPop.contains(ziel))) return;
    hinweisPopSchliessen();
  };
  hinweisBtn.addEventListener('click', () => {
    const oeffnen = hinweisPop.hidden;
    hinweisPop.hidden = !oeffnen;
    hinweisBtn.setAttribute('aria-expanded', oeffnen ? 'true' : 'false');
  });
  // Ein Klick daneben schliesst das Popover. ESC schliesst dagegen das GANZE Modal und nicht
  // nur den Erklaertext: openModal haengt seinen keydown-Handler frueher an `document` und
  // ruft stopPropagation, ein eigener Handler kaeme dort nicht mehr an. Das ist hingenommen --
  // der Erklaertext ist kein Zustand, den man einzeln verlassen muesste.
  document.addEventListener('click', hinweisKlickDraussen);

  // Aufklappzustand des Baums, absichtlich NEBEN `state`: er gehoert rein zur Anzeige und
  // spielt beim Speichern keine Rolle. Er ueberlebt eine Suche -- wer etwas tippt und den
  // Suchbegriff wieder loescht, findet seinen Ast wieder aufgeklappt vor.
  let baumIndex: KategorieBaumIndex = { kinder: new Map(), wurzeln: [] };
  const offeneKnoten = new Set<number>();

  const state: KategorieAuswahlState = {
    katalog: new Map(),
    blaetter: new Set(),
    treffer: [],
    trefferGesamt: 0,
    suche: '',
    gewaehlt: new Map(),
    teilZaehler: new Map(),
    sammelwurzelAusblenden: false,
    urspruenglich: new Set(),
  };

  // ----- Zustand -> Anzeige -------------------------------------------------

  /** Setzt die Teil-Anzeige genau der uebergebenen Elternknoten neu, sofern sie gerade
   *  sichtbar sind. Bewusst kein Neu-Rendern des Baums: das wuerde jeden aufgeklappten Ast
   *  zuklappen. Die Liste kommt aus zaehlerAendern und ist so lang wie der Weg zur Wurzel. */
  const aktualisiereTeilAnzeige = (ahnen: number[]) => {
    for (const ahn of ahnen) {
      resultsEl
        .querySelector<HTMLElement>(`[data-kat-teil="${ahn}"]`)
        ?.classList.toggle('is-on', istTeilweiseGewaehlt(state.teilZaehler, ahn));
    }
  };

  const bindAuswahl = () => {
    selectedEl.querySelectorAll<HTMLButtonElement>('button[data-kat-weg]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = Number(btn.dataset.katWeg);
        if (Number.isFinite(id)) setzeAuswahl(id, false);
      });
    });
  };

  /** Zeichnet die rechte Spalte, ihren Zaehler, den Zustand von "Alle entfernen", die
   *  Statuszeile der Fussleiste und den Speichern-Knopf. Alles, was von der Groesse der
   *  Auswahl abhaengt, steht hier an einer Stelle. */
  const aktualisiereAuswahl = () => {
    selectedEl.innerHTML = renderKategorieAusgewaehlt(state);
    bindAuswahl();
    const anzahl = state.gewaehlt.size;
    auswahlZahl.textContent = anzahl.toLocaleString('de-DE');
    alleWegBtn.disabled = anzahl === 0;
    if (anzahl === 0) entfernenZuruecksetzen();
    fussStatus.innerHTML = kategorieStatusText(anzahl, state.katalog.size);
  };

  /** Einziger Weg, eine Kategorie an- oder abzuwaehlen -- Baum, Trefferliste und Chips laufen
   *  alle hier durch. Deshalb koennen die drei Ansichten nicht auseinanderlaufen, ohne dass
   *  irgendetwas neu gerendert werden muesste. */
  const setzeAuswahl = (id: number, an: boolean) => {
    if (an === state.gewaehlt.has(id)) return;
    if (an) state.gewaehlt.set(id, 'manual');
    else state.gewaehlt.delete(id);
    aktualisiereTeilAnzeige(zaehlerAendern(state.teilZaehler, state.katalog, id, an ? 1 : -1));
    // Gegenrichtung: wird eine Kategorie rechts entfernt, muss ihr Haken auch links
    // verschwinden -- egal ob dort gerade der Baum oder die Trefferliste steht. Bewusst NUR
    // die checked-Eigenschaft, KEIN Neu-Rendern: das wuerde jeden aufgeklappten Ast zuklappen.
    resultsEl.querySelectorAll<HTMLInputElement>(`input[data-kat-id="${id}"]`).forEach((cb) => {
      cb.checked = an;
    });
    aktualisiereAuswahl();
  };

  /** Haengt die change-Handler an die Checkboxen des Ergebnisbereichs. `marker` unterscheidet
   *  Baum (`data-kat-baum`) und Trefferliste (`data-kat-treffer`) -- beide verhalten sich
   *  gleich, aber nur eine Art steht gleichzeitig im DOM. */
  const bindCheckboxen = (marker: 'data-kat-baum' | 'data-kat-treffer') => {
    resultsEl.querySelectorAll<HTMLInputElement>(`input[${marker}]`).forEach((cb) => {
      cb.addEventListener('change', () => {
        const id = Number(cb.dataset.katId);
        if (Number.isFinite(id)) setzeAuswahl(id, cb.checked);
        else cb.checked = false;
      });
    });
  };

  const bindBaum = () => {
    bindCheckboxen('data-kat-baum');
    resultsEl.querySelectorAll<HTMLButtonElement>('button[data-kat-auf]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = Number(btn.dataset.katAuf);
        if (!Number.isFinite(id)) return;
        // Ein Klick auf einen Elternknoten klappt auf oder zu, mehr nicht. Er waehlt NICHTS
        // aus (Vorgabe des Betreibers): Kaufland laesst Angebote nur in Blattkategorien zu.
        if (offeneKnoten.has(id)) offeneKnoten.delete(id);
        else offeneKnoten.add(id);
        // Mit der ID: nach dem Neurendern soll der Fokus wieder auf genau diesem Knopf sitzen.
        zeigeBaum(id);
      });
    });
  };

  /** Rendert den Baum neu. `fokusId` ist die Kategorie, deren Aufklapp-Knopf danach wieder den
   *  Fokus bekommt.
   *
   *  Warum Scrollposition und Fokus von Hand gerettet werden: Das Neurendern ersetzt den
   *  kompletten Inhalt von `resultsEl`, und der Bereich hat einen eigenen Scrollbereich. Ohne
   *  die drei Zeilen spraenge die Liste bei JEDEM Aufklappen an den Anfang zurueck, der
   *  angeklickte Knoten waere aus dem Bild und der Tastaturfokus verloren. Aufklappen ist die
   *  Kerninteraktion des Baums, das traefe jeden Klick.
   *
   *  `preventScroll: true` ist kein Detail: ohne die Option scrollt `focus()` den Knopf selbst
   *  ins Bild und macht die gerade wiederhergestellte Scrollposition wieder kaputt.
   *
   *  Der Knoten kann durch sein eigenes Umschalten nicht verschwinden -- ein Klick aendert nur,
   *  ob seine KINDER gerendert werden, er selbst war sichtbar und bleibt es. Das `?.` ist
   *  trotzdem da, damit ein kuenftiger Aufrufer mit einer inzwischen unsichtbaren ID keinen
   *  Laufzeitfehler ausloest. */
  const zeigeBaum = (fokusId?: number) => {
    const scroll = resultsEl.scrollTop;
    resultsEl.innerHTML = renderKategorieBaum(
      state.katalog,
      baumIndex,
      offeneKnoten,
      state.gewaehlt,
      state.teilZaehler,
    );
    bindBaum();
    resultsEl.scrollTop = scroll;
    if (fokusId !== undefined) {
      resultsEl
        .querySelector<HTMLButtonElement>(`button[data-kat-auf="${fokusId}"]`)
        ?.focus({ preventScroll: true });
    }
  };

  /** Kopfzeile der linken Spalte: Ueberschrift, Zaehler-Pille und die beiden Klapp-Links.
   *  Die Links haengen am Baum -- auf der Trefferliste gibt es nichts zu klappen. */
  const aktualisiereKopfLinks = () => {
    const suchend = state.suche !== '';
    const baumDa = !suchend && state.katalog.size > 0;
    linkeUeberschrift.textContent = suchend ? 'Suchergebnisse' : 'Kategoriebaum';
    if (suchend) {
      linkeZahl.textContent = `${state.trefferGesamt.toLocaleString('de-DE')} Treffer`;
      linkeZahl.hidden = false;
    } else if (state.katalog.size > 0) {
      linkeZahl.textContent = `${state.katalog.size.toLocaleString('de-DE')} gesamt`;
      linkeZahl.hidden = false;
    } else {
      linkeZahl.hidden = true;
    }
    aufklappBtn.disabled = !baumDa;
    zuklappBtn.disabled = !baumDa;
  };

  // ----- Bedienung ----------------------------------------------------------

  aufklappBtn.addEventListener('click', () => {
    const ergebnis = ebenenweiseAufklappen(baumIndex);
    offeneKnoten.clear();
    for (const id of ergebnis.offen) offeneKnoten.add(id);
    // Von vorn anzeigen: nach einem Aufklappen ueber mehrere Ebenen bezeichnet die alte
    // Scrollposition nicht mehr dieselbe Stelle im Baum.
    resultsEl.scrollTop = 0;
    zeigeBaum();
    resultsEl.scrollTop = 0;
    if (ergebnis.vollstaendig) {
      hinweisWeg();
      return;
    }
    // Der Deckel wird BENANNT und nicht verschwiegen. Bei 6.087 Kategorien ist "alles" nicht
    // darstellbar; ein Knopf, der stillschweigend weniger tut als er verspricht, laesst den
    // Haendler die fehlenden Aeste fuer nicht vorhanden halten.
    zeigeHinweis(
      `Bis Ebene ${ergebnis.ebenen} aufgeklappt, das sind ${ergebnis.zeilen.toLocaleString('de-DE')} Zeilen. ` +
        `Tiefer geht es je Ast einzeln: alles auf einmal wären ${state.katalog.size.toLocaleString('de-DE')} Zeilen, ` +
        'und so viele auf einmal zu zeichnen lässt das Fenster hängen.',
    );
  });

  zuklappBtn.addEventListener('click', () => {
    offeneKnoten.clear();
    // Die Vorbelegung wieder herstellen statt alles zuzuklappen: Kauflands Katalog haengt
    // alles unter EINEN Sammelknoten. Voellig zugeklappt bliebe genau eine Zeile stehen, und
    // der Baum saehe aus, als waere er leer.
    for (const id of anfangsOffeneKnoten(baumIndex)) offeneKnoten.add(id);
    resultsEl.scrollTop = 0;
    zeigeBaum();
    resultsEl.scrollTop = 0;
    hinweisWeg();
  });

  const entfernenZuruecksetzen = () => {
    if (entfernenTimer !== null) window.clearTimeout(entfernenTimer);
    entfernenTimer = null;
    alleWegBtn.textContent = 'Alle entfernen';
    alleWegBtn.classList.remove('is-armed');
  };

  alleWegBtn.addEventListener('click', () => {
    // Zweistufiger Klick statt window.confirm -- Browser-Dialoge sind in dieser App verboten
    // (Projektvorgabe). Die Aktion ist zwar bis zum Speichern umkehrbar (Abbrechen verwirft
    // alles), aber einige hundert Haken durch einen Fehlklick zu verlieren ist teuer genug
    // fuer eine Rueckfrage.
    if (!alleWegBtn.classList.contains('is-armed')) {
      alleWegBtn.classList.add('is-armed');
      alleWegBtn.textContent = 'Wirklich alle entfernen?';
      entfernenTimer = window.setTimeout(entfernenZuruecksetzen, KATEGORIE_ENTFERNEN_FRIST_MS) as unknown as number;
      return;
    }
    entfernenZuruecksetzen();
    // In einem Zug statt setzeAuswahl je Eintrag: bei einigen hundert gewaehlten Blaettern
    // waeren das ebenso viele vollstaendige Neuaufbauten der rechten Spalte.
    state.gewaehlt.clear();
    state.teilZaehler.clear();
    resultsEl.querySelectorAll<HTMLInputElement>('input[data-kat-id]').forEach((cb) => {
      cb.checked = false;
    });
    resultsEl.querySelectorAll<HTMLElement>('[data-kat-teil]').forEach((el) => {
      el.classList.remove('is-on');
    });
    aktualisiereAuswahl();
  });

  const runSearch = (term: string) => {
    const trimmed = term.trim();
    state.suche = trimmed;
    if (trimmed === '') {
      state.treffer = [];
      state.trefferGesamt = 0;
      // Leeres Suchfeld -> Baum, an derselben Stelle. Der Aufklappzustand steht in
      // offeneKnoten und hat die Suche ueberdauert.
      //
      // Bei leerem Katalog passiert hier NICHTS: dann steht im Ergebnisbereich der gesperrte
      // Hinweis aus dem Ladepfad, und ein leerer Baum waere eine zweite Meldung fuer denselben
      // Zustand. Erreichbar ist der Fall ohnehin kaum -- Suchfeld und Loeschen-Knopf bleiben
      // dort deaktiviert bzw. verborgen.
      if (state.katalog.size > 0) zeigeBaum();
      aktualisiereKopfLinks();
      return;
    }
    const ergebnis = sucheKategorien(state.katalog, state.blaetter, trimmed, state.sammelwurzelAusblenden);
    state.treffer = ergebnis.treffer;
    state.trefferGesamt = ergebnis.gesamt;
    resultsEl.innerHTML = renderKategorieTreffer(state);
    bindCheckboxen('data-kat-treffer');
    aktualisiereKopfLinks();
    hinweisWeg();
  };

  const updateClearVisibility = () => { clearBtn.hidden = searchInput.value.length === 0; };
  searchInput.addEventListener('input', () => {
    updateClearVisibility();
    if (debounceTimer !== null) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => runSearch(searchInput.value), KATEGORIE_SEARCH_DEBOUNCE_MS) as unknown as number;
  });
  clearBtn.addEventListener('click', () => {
    searchInput.value = '';
    updateClearVisibility();
    runSearch('');
    searchInput.focus();
  });

  saveBtn.addEventListener('click', () => {
    clearError();
    // Zum Zeitpunkt des Klicks lesen, nicht beim Oeffnen: der Haken kann bis zuletzt
    // umgestellt werden. Fehlt das Kaestchen (nur ein aktiver Kanal), bleibt die Liste leer
    // und speichernKategorien ueberspringt den Vererbungsschritt vollstaendig.
    // Nicht ueber q(): der Helfer erzwingt mit `!` ein Ergebnis, das Kaestchen kann aber
    // legitim fehlen (nur ein aktiver Kanal).
    const vererbenBox = modal.el.querySelector<HTMLInputElement>('#kl-kat-vererben');
    const vererben = vererbenBox?.checked === true;
    const ziele = vererben ? vererbungsKanaele : [];
    void speichernKategorien(ctx, storefront, state, ziele, saveBtn, showError, (jobs, vererbtText) => {
      const basis =
        jobs > 0
          ? `Kategorien gespeichert. Auswahlwerte werden im Hintergrund geladen (${jobs} Job${jobs === 1 ? '' : 's'}).`
          : 'Kategorien gespeichert.';
      showToast(vererbtText ? `${basis} ${vererbtText}` : basis);
      modal.close();
      onSaved();
    });
  });

  void (async () => {
    try {
      // Beide Abrufe blaettern (ladeAlleSeiten): PostgREST kappt bei max_rows = 1000 lautlos.
      // Beim Katalog hiess das frueher "die Kategorie gibt es bei Kaufland nicht"; bei der
      // AUSWAHL waere es schlimmer, weil speichernKategorien() den geladenen Stand als
      // vollstaendig behandelt und kaufland_set_tenant_categories jede nicht uebergebene
      // manuelle Zeile loescht. Ein abgeschnittener Ladevorgang wuerde beim naechsten
      // Speichern also stillschweigend alles jenseits von Platz 1000 wegwerfen -- und der
      // Baum laedt geradezu dazu ein, viele Blaetter anzuhaken.
      const [{ katalog, blaetter }, auswahl] = await Promise.all([
        ladeKategorieKatalogCached(ctx.supabase, storefront),
        ladeGewaehlteKategorien(ctx.supabase, storefront),
      ]);

      state.katalog = katalog;
      state.blaetter = blaetter;
      for (const row of auswahl) state.gewaehlt.set(row.categoryId, row.addedBy);
      // Schnappschuss der beim Laden vorgefundenen Auswahl -- Vergleichsbasis fuer
      // speichernKategorien(), um abgewaehlte Zeilen zu erkennen (siehe dort).
      state.urspruenglich = new Set(state.gewaehlt.keys());
    } catch (e) {
      showError(`Kategorien konnten nicht geladen werden: ${(e as Error).message}`);
      selectedEl.innerHTML = renderKatEmpty('fa-triangle-exclamation', 'Keine Daten geladen.');
      return;
    }

    // Leerer Katalog ist KEIN Fehler und landet deshalb nicht im catch darueber: der Abruf
    // gelingt, er liefert nur nichts. Live ist das kein theoretischer Fall -- der Mandant hat
    // vier aktive Storefronts (at, de, fr, nl), und `nl` fuehrt in kaufland.categories null
    // Zeilen, waehrend at/de/fr je 6.087 haben. Ohne diesen Zweig blieben Suche und Speichern
    // freigeschaltet, jede Eingabe antwortete "Keine Treffer", und der Haendler suchte den
    // Fehler bei seinem Suchbegriff statt beim fehlenden Katalog.
    //
    // Suchfeld und Speichern bleiben deshalb deaktiviert (beide starten so aus
    // renderKategorieModalBody), ebenso die beiden Klapp-Links. Eine Auswahl kann es hier
    // nicht geben: kaufland_set_tenant_categories (Migration 0043) laesst nur Kategorien zu,
    // die der Katalog DIESER Storefront fuehrt, bei leerem Katalog also keine einzige.
    //
    // Der Text nennt bewusst KEINEN Mechanismus und KEINEN Zeitpunkt. Eine Vorfassung sagte
    // "ein Hintergrund-Job holt ihn von Kaufland". Das ist live widerlegt: alle sieben
    // kaufland.refresh_categories-Jobs der letzten Woche stehen auf failed mit
    // "needs KAUFLAND_SYSTEM_CLIENT_KEY + KAUFLAND_SYSTEM_SECRET_KEY for tenant-less jobs",
    // und die beiden Variablen sind in der Worker-App gar nicht gesetzt. Fuer `nl` gab es nie
    // einen Lauf. Der Haendler wuerde also auf etwas warten, das ohne Konfigurationsaenderung
    // nie eintritt. Deshalb: Zustand benennen, an den Support verweisen, nichts versprechen.
    if (state.katalog.size === 0) {
      subtitleEl.textContent = `Kaufland ${label} · kein Katalog`;
      resultsEl.innerHTML = renderKatEmpty('fa-triangle-exclamation', 'Ohne Katalog lässt sich hier nichts suchen.');
      selectedEl.innerHTML = renderKatEmpty(
        'fa-triangle-exclamation',
        'Für diesen Marktplatz liegt kein Kaufland-Kategorie-Katalog vor. Ohne ihn lässt sich hier keine Kategorie auswählen. Bitte wenden Sie sich an den Support.',
      );
      aktualisiereKopfLinks();
      fussStatus.innerHTML = kategorieStatusText(0, 0);
      return;
    }

    // Erst hier, nicht frueher: vorher gibt es keinen Katalog, und der Ladehinweis im
    // Ergebnisbereich darf nicht von einem leeren Baum ueberschrieben werden.
    baumIndex = baueKategorieBaum(state.katalog);
    // Der Sammelknoten wird im Pfad nur dann weggelassen, wenn es genau EINEN gibt -- bei
    // mehreren Wurzeln ist der oberste Name Information und keine Wiederholung.
    state.sammelwurzelAusblenden = baumIndex.wurzeln.length === 1;
    // Teil-Anzeige einmal aus der geladenen Auswahl aufbauen. Danach wird sie nur noch
    // fortgeschrieben (setzeAuswahl -> zaehlerAendern), nie neu gerechnet.
    state.teilZaehler = baueAhnenZaehler(state.katalog, state.gewaehlt.keys());

    subtitleEl.textContent = `Kaufland ${label} · ${state.katalog.size.toLocaleString('de-DE')} Kategorien`;
    aktualisiereAuswahl();
    // Vorbelegung GENAU EINMAL, hier beim Aufbau und nicht in zeigeBaum(): Kauflands Katalog
    // haengt alles unter einen einzigen Sammelknoten (fuer `de` die Wurzel mit 13 direkten
    // Kindern), sonst stuende beim Oeffnen nur eine Zeile da. Stuende die Vorbelegung im
    // Renderpfad, koennte der Haendler diese Wurzel nie zuklappen -- das naechste Rendern
    // wuerde sie sofort wieder aufklappen.
    for (const id of anfangsOffeneKnoten(baumIndex)) offeneKnoten.add(id);
    zeigeBaum();
    aktualisiereKopfLinks();
    searchInput.disabled = false;
    saveBtn.disabled = false;
  })();
}

// ----- Category status block -----------------------------------------------

async function fetchCategoryStatus(ctx: SettingsCtx): Promise<CategoryStatusRow[]> {
  const { data, error } = await ctx.supabase.rpc('kaufland_get_category_status');
  if (error) {
    console.warn('[settings] kaufland_get_category_status error', error);
    return [];
  }
  return (data ?? []) as CategoryStatusRow[];
}

/** Zaehlt die Auswahl je Storefront (kaufland.tenant_categories, RLS filtert bereits auf den
 *  Mandanten). Ersetzt die fruehere Differenz-Anzeige "X von Y Kategorien synchron", die seit
 *  dem selektiven Push (Task 3) keinen Sinn mehr ergibt -- es wird bewusst nicht mehr der
 *  gesamte Katalog gepusht, ein "fehlt" gegen 6.087 Zeilen waere deshalb irrefuehrend.
 *
 *  EINE Abfrage, client-seitig gruppiert. Die Vorfassung feuerte je Poll-Tick einen
 *  HEAD-Request pro Storefront aus einer hartcodierten Liste von sieben Codes. Die Liste war
 *  falsch: `kaufland.storefronts` fuehrt live zehn (`at, cz, de, es, fr, hr, it, nl, pl, sk`),
 *  seit Migration 0041 haengt die Pruefung in `kaufland_enqueue_category_push` an genau diesem
 *  Katalog statt an einer CHECK-Klausel. Ein Haendler mit HR-, NL- oder ES-Storefront bekam
 *  deshalb nie eine Zaehlung und las dauerhaft "Noch keine Kategorie ausgewaehlt". Ohne die
 *  Liste ist jede Storefront abgedeckt, auch jede kuenftige.
 *
 *  Die Abfrage BLAETTERT (Review-Befund 2026-08-09). Eine Vorfassung hielt PostgRESTs
 *  `max_rows` von 1000 hier fuer unkritisch, weil die Zeilen ja "die Auswahl eines Mandanten
 *  sind, nicht der Katalog -- Dutzende je Storefront". Diese Begruendung traegt nicht:
 *  Erstens ist es genau die Annahme, die im selben Vorhaben schon zweimal falsch war (beim
 *  Katalog-Lader und beim Laden der Auswahl im Modal). Zweitens zaehlt diese Abfrage ueber
 *  ALLE Storefronts zusammen, das Limit ist also frueher erreicht als "Dutzende je Storefront"
 *  vermuten laesst -- bei zehn Storefronts reichen im Schnitt 100 je Stueck. Drittens ist der
 *  Kategoriebaum genau das Werkzeug, mit dem ein Haendler schnell viele Blaetter anhakt.
 *  Die Folge waere milder als beim Speichern, aber sichtbar falsch: Die Kachel meldete ab
 *  1.000 Auswahlzeilen zu wenig, und an der Zahl allein erkennt das niemand.
 *
 *  `order('storefront')` ist fuer das Blaettern zwingend, nicht Kosmetik: Ohne feste
 *  Sortierung darf Postgres die Zeilen zwischen zwei Seiten anders anordnen, dann fehlen
 *  Zeilen in der einen und tauchen in der naechsten doppelt auf -- und beides veraendert
 *  genau die Zahl, um die es hier geht. */
export async function fetchSelectedCategoryCounts(ctx: SettingsCtx): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  try {
    const rows = await ladeAlleSeiten<{ storefront: string }>((von, bis) =>
      ctx.supabase
        .schema('kaufland')
        .from('tenant_categories')
        .select('storefront')
        .order('storefront', { ascending: true })
        .range(von, bis),
    );
    for (const row of rows) {
      counts.set(row.storefront, (counts.get(row.storefront) ?? 0) + 1);
    }
  } catch (e) {
    // Unveraendertes Verhalten im Fehlerfall: leise leere Zaehlung statt einer Fehlermeldung.
    // Die Kacheln zeigen dann "Noch keine Kategorie ausgewaehlt" -- der Aufrufer haengt an
    // einem 10-Sekunden-Poll, eine Fehlerzeile je Tick waere schlimmer als die stille Null.
    // ladeAlleSeiten wirft, wo die Vorfassung ein error-Objekt zurueckgab, deshalb try/catch.
    console.warn('[settings] tenant_categories count fetch failed', (e as Error).message);
  }
  return counts;
}

function applyCategoryStatus(
  listEl: HTMLElement,
  rows: CategoryStatusRow[],
  selectedCounts: Map<string, number>,
  ctx: SettingsCtx,
): void {
  const byStorefront = new Map<string, CategoryStatusRow>();
  for (const r of rows) byStorefront.set(r.storefront, r);

  listEl.querySelectorAll<HTMLElement>('.kl-storefront-card').forEach((card) => {
    const code = card.dataset.storefront!;
    const placeholder = card.querySelector<HTMLDivElement>('[data-categories-block]');
    if (!placeholder) return; // Only active cards have one.
    const data = byStorefront.get(code);
    placeholder.innerHTML = renderCategoryBlock(code, data, selectedCounts.get(code) ?? 0);
    placeholder.querySelectorAll<HTMLButtonElement>('button[data-action="choose-categories"]').forEach((btn) => {
      btn.addEventListener('click', () => onChooseCategoriesClick(btn, card, ctx, listEl));
    });
  });
}

/** Design "Kaufland Einstellungen": .mp-stat (Zähler) + .mp-sync (Status) + .mp-btn. Der Knopf
 *  "Kategorien wählen" ist IMMER da und NIE deaktiviert -- waehlen kann der Haendler jederzeit,
 *  unabhaengig davon ob der Katalog noch laedt oder gerade ein Push laeuft (Task 5 Brief). */
function renderCategoryBlock(storefront: string, row: CategoryStatusRow | undefined, selectedCount: number): string {
  const chooseButton = `
    <button type="button" class="mp-btn" data-action="choose-categories" data-storefront="${storefront}">
      <span class="v-icon fa-regular fa-list-check"></span>
      Kategorien wählen
    </button>
  `;

  // Der Text sagte hier "Kategorien werden gerade aus Kaufland geladen (Hintergrund-Job,
  // woechentlich) …" und behauptete damit einen laufenden Abruf. Fuer `nl` laeuft live
  // nachweislich nichts: kaufland.categories hat null Zeilen und kaufland.category_sync_runs
  // keinen einzigen Lauf. Der Zweig trifft "Katalog ist nicht da" -- ob dazu gerade etwas
  // laeuft, weiss er nicht.
  //
  // Auch die Zwischenfassung "ein Hintergrund-Job holt ihn von Kaufland" ist raus: der
  // woechentliche Crawl scheitert live fuer JEDE Storefront an zwei fehlenden Worker-Variablen
  // (KAUFLAND_SYSTEM_CLIENT_KEY, KAUFLAND_SYSTEM_SECRET_KEY), zuletzt am 2026-08-09 03:00. Ein
  // Versprechen, das der Betrieb nicht einloest, ist schlechter als eine nuechterne Ansage.
  // Gleiche Aussage wie im Modal, damit Kachel und Modal dieselbe Geschichte erzaehlen.
  if (!row || row.kaufland_count === 0) {
    return `
      <div class="mp-sync warn"><span class="d"></span>Für diesen Marktplatz liegt kein Kategorie-Katalog vor. Bitte wenden Sie sich an den Support.</div>
      ${chooseButton}
    `;
  }

  if (row.run_status === 'running' || row.active_job_id) {
    // categories_total/-fetched zaehlen seit Task 3 nur noch die Auswahl plus deren Elternkette,
    // nicht mehr den gesamten Katalog -- ein Vergleich mit kaufland_count waere hier ebenso
    // irrefuehrend wie in der Ruhe-Ansicht unten, deshalb bewusst weggelassen.
    const total = row.categories_total;
    const done = row.categories_fetched;
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    const phaseLabel = formatPhase(row.run_phase);
    const fortschritt = total > 0 ? ` · <b>${done.toLocaleString('de-DE')} / ${total.toLocaleString('de-DE')}</b> (${pct} %)` : '';
    return `
      <div class="mp-sync syncing"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span>${escapeHtml(phaseLabel)}${fortschritt}</div>
      <div class="mp-note">kann bis zu einer Stunde dauern</div>
      ${chooseButton}
    `;
  }

  const anzahlZeile = selectedCount > 0
    ? `<div class="mp-stat">${SVG_TAGS}<b>${selectedCount.toLocaleString('de-DE')}</b> Kategorie${selectedCount === 1 ? '' : 'n'} ausgewählt</div>`
    : '';

  // Ein fehlgeschlagener Lauf braucht eine eigene Zeile: `last_synced_at` ist in
  // kaufland_get_category_status schlicht `finished_at` des juengsten Laufs, unabhaengig von
  // dessen Status. Ohne diese Unterscheidung las der Haendler "N Kategorien ausgewählt · letzter
  // Sync vor 3 Minuten", obwohl bei Vario nichts angekommen ist.
  if (row.run_status === 'failed') {
    const versuch = row.last_synced_at ? ` <span class="when">· letzter Versuch ${escapeHtml(formatRelative(row.last_synced_at))}</span>` : '';
    return `
      ${anzahlZeile}
      <div class="mp-sync warn"><span class="d"></span>Sync fehlgeschlagen${versuch}</div>
      ${chooseButton}
    `;
  }

  if (selectedCount === 0) {
    return `
      <div class="mp-sync warn"><span class="d"></span>Noch keine Kategorie ausgewählt.</div>
      ${chooseButton}
    `;
  }

  // Bewusst "Sync", nicht "Push": `latest_runs` in kaufland_get_category_status filtert nicht auf
  // `kind`, und die Tabelle kaufland.category_sync_runs fuehrt beide Arten (`pull` vom
  // Katalog-Abruf, `push` nach Vario). Der Zeitstempel kann also von einem Pull stammen.
  const sub = row.last_synced_at ? ` <span class="when">· letzter Sync ${escapeHtml(formatRelative(row.last_synced_at))}</span>` : '';

  return `
    <div class="mp-stat">${SVG_TAGS}<b>${selectedCount.toLocaleString('de-DE')}</b> Kategorie${selectedCount === 1 ? '' : 'n'} ausgewählt${sub}</div>
    ${chooseButton}
  `;
}

function formatPhase(phase: string | null): string {
  if (!phase) return 'läuft';
  if (phase === 'pull') return 'lade Kaufland-Katalog';
  if (phase === 'ensure-tree') return 'Vario-Baum vorbereiten';
  if (phase === 'compare') return 'Vergleich mit Vario';
  if (phase.startsWith('push:level-')) return `Push Ebene ${phase.slice('push:level-'.length)}`;
  return phase;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  if (diffMs < 60_000) return `vor ${Math.max(1, Math.round(diffMs / 1000))} s`;
  if (diffMs < 3_600_000) return `vor ${Math.round(diffMs / 60_000)} min`;
  if (diffMs < 86_400_000) return `vor ${Math.round(diffMs / 3_600_000)} h`;
  return new Date(iso).toLocaleDateString('de-DE');
}

/** Oeffnet das Kategorie-Modal fuer die Storefront dieser Kachel. Das Label kommt aus
 *  data-storefront-label statt aus dem sichtbaren Text (".mp-name"), damit ein Redesign des
 *  Kopf-Markups diese Stelle nicht stillschweigend zerbricht. */
function onChooseCategoriesClick(btn: HTMLButtonElement, card: HTMLElement, ctx: SettingsCtx, listEl: HTMLElement): void {
  const storefront = btn.dataset.storefront!;
  const label = card.dataset.storefrontLabel ?? storefront.toUpperCase();
  // Zielkanäle der Vererbung: die übrigen AKTIVEN Storefronts. Erkennungsmerkmal ist der
  // Kategorie-Block, den applyCategoryStatus nur in aktive Kacheln setzt ("Only active cards
  // have one") -- damit ist die Liste per Konstruktion dieselbe Menge, für die es den Knopf
  // "Kategorien wählen" überhaupt gibt, und sie kann nicht auseinanderlaufen.
  const aktive = [...listEl.querySelectorAll<HTMLElement>('.kl-storefront-card')]
    .filter((c) => c.querySelector('[data-categories-block]') !== null)
    .map((c) => c.dataset.storefront ?? '');
  openKategorieModal(ctx, storefront, label, vererbungsZiele(aktive, storefront), () => {
    void nachSpeichernPushAnstossen(ctx, storefront, listEl);
  });
}

/** Fix-Runde 1, Befund 1 (kritisch): Live widerlegt war die Annahme, eine gespeicherte
 *  Auswahl erreiche Vario "beim naechsten Sonntagslauf" -- `kaufland-categories-weekly`
 *  reiht ausschliesslich `kaufland.refresh_categories`/`kaufland.refresh_vat_indicators` ein,
 *  NIE `kaufland.push_categories_to_vario`, und `kaufland_enqueue_category_push` hatte
 *  repoweit keinen einzigen Aufrufer mehr (die fuenf Zeilen in `account.jobs` waren von Hand
 *  eingereiht, die juengste vom 2026-08-05). Ohne diesen Aufruf waere eine gespeicherte
 *  Auswahl reine Datenbank-Kosmetik: nie ein Knoten in Varios Kategoriebaum. Die RPC ist
 *  idempotent (meldet `enqueued=false`, wenn fuer diese Storefront schon ein Job offen ist)
 *  und fuer `authenticated` freigegeben (beides live geprueft) -- ein Fehlschlag hier darf
 *  deshalb nur informieren, nicht die bereits erfolgreich gespeicherte Auswahl in Frage
 *  stellen. Der anschliessende Poll zeigt den (neuen oder bereits laufenden) Fortschritt im
 *  laengst vorhandenen "running"-Zweig von renderCategoryBlock. */
async function nachSpeichernPushAnstossen(ctx: SettingsCtx, storefront: string, listEl: HTMLElement): Promise<void> {
  try {
    const { error } = await ctx.supabase.rpc('kaufland_enqueue_category_push', { _storefront: storefront });
    if (error) {
      showToast(`Push nach Vario konnte nicht gestartet werden: ${error.message}`, 'error');
    }
  } catch (e) {
    showToast(`Push nach Vario konnte nicht gestartet werden: ${(e as Error).message}`, 'error');
  }
  // Sofort einmal aktualisieren (zeigt ggf. schon "laeuft") und danach pollen, bis fertig --
  // unabhaengig davon, ob HIER ein neuer Job entstand oder fuer diese Storefront schon einer lief.
  void pollCategoryStatusOnce(listEl, ctx);
  startCategoryPoll(listEl, ctx);
}

async function pollCategoryStatusOnce(listEl: HTMLElement, ctx: SettingsCtx): Promise<void> {
  try {
    const [rows, selectedCounts] = await Promise.all([fetchCategoryStatus(ctx), fetchSelectedCategoryCounts(ctx)]);
    applyCategoryStatus(listEl, rows, selectedCounts, ctx);
  } catch (e) {
    console.warn('[settings] initial category status fetch failed', e);
  }
}

function startCategoryPoll(listEl: HTMLElement, ctx: SettingsCtx): void {
  stopCategoryPoll();
  categoryPollTimer = setInterval(async () => {
    try {
      const [rows, selectedCounts] = await Promise.all([fetchCategoryStatus(ctx), fetchSelectedCategoryCounts(ctx)]);
      applyCategoryStatus(listEl, rows, selectedCounts, ctx);
      // Stop once nothing's running any more.
      if (!rows.some((r) => r.active_job_id || r.run_status === 'running')) {
        stopCategoryPoll();
      }
    } catch (e) {
      console.warn('[settings] category poll failed', e);
    }
  }, CATEGORY_POLL_INTERVAL_MS);
}

function stopCategoryPoll(): void {
  if (categoryPollTimer) {
    clearInterval(categoryPollTimer);
    categoryPollTimer = null;
  }
}

// ----- Shipping-Groups status block ---------------------------------------

async function fetchShippingGroupStatus(ctx: SettingsCtx): Promise<ShippingGroupStatusRow[]> {
  const { data, error } = await ctx.supabase
    .schema('kaufland')
    .from('shipping_groups')
    .select('storefront, id_shipping_group, name, fetched_at')
    .order('storefront')
    .order('name');
  if (error) {
    console.warn('[settings] shipping_groups status fetch failed', error.message);
    return [];
  }

  type SgRow = { storefront: string; id_shipping_group: string; name: string | null; fetched_at: string | null };
  const byStorefront = new Map<string, {
    count: number;
    latest: string | null;
    groups: Array<{ id_shipping_group: string; name: string | null }>;
  }>();
  for (const r of (data ?? []) as SgRow[]) {
    const slot = byStorefront.get(r.storefront) ?? { count: 0, latest: null, groups: [] };
    slot.count += 1;
    if (r.fetched_at && (!slot.latest || r.fetched_at > slot.latest)) slot.latest = r.fetched_at;
    slot.groups.push({ id_shipping_group: r.id_shipping_group, name: r.name });
    byStorefront.set(r.storefront, slot);
  }

  // Standard-Versandgruppe je Storefront aus kaufland.offer_default_profiles
  // (category_id IS NULL = globales Profil). Parallel pro storefront via Get-RPC.
  const storefronts = Array.from(byStorefront.keys());
  const profilePromises = storefronts.map((sf) =>
    ctx.supabase.rpc('kaufland_get_offer_default_profile', { _storefront: sf }),
  );
  const profileResults = await Promise.all(profilePromises);
  const defaultBySf = new Map<string, string | null>();
  for (let i = 0; i < storefronts.length; i++) {
    const sf = storefronts[i]!;
    const res = profileResults[i]!;
    if (res.error) {
      console.warn('[settings] offer_default_profile fetch failed for', sf, res.error.message);
      defaultBySf.set(sf, null);
      continue;
    }
    const payload = (res.data ?? {}) as { global?: { id_shipping_group?: string | null } | null };
    defaultBySf.set(sf, payload.global?.id_shipping_group ?? null);
  }

  return storefronts.map((storefront) => {
    const slot = byStorefront.get(storefront)!;
    return {
      storefront,
      count: slot.count,
      lastSyncedAt: slot.latest,
      groups: slot.groups,
      defaultShippingGroupId: defaultBySf.get(storefront) ?? null,
    };
  });
}

function applyShippingGroupStatus(
  listEl: HTMLElement,
  rows: ShippingGroupStatusRow[],
  ctx: SettingsCtx,
): void {
  const byStorefront = new Map<string, ShippingGroupStatusRow>();
  for (const r of rows) byStorefront.set(r.storefront, r);

  listEl.querySelectorAll<HTMLElement>('.kl-storefront-card').forEach((card) => {
    const code = card.dataset.storefront!;
    const placeholder = card.querySelector<HTMLDivElement>('[data-shipping-groups-block]');
    if (!placeholder) return; // Nur aktive Cards haben den Block.
    const row = byStorefront.get(code);
    placeholder.innerHTML = renderShippingGroupBlock(code, row);
    placeholder.querySelectorAll<HTMLButtonElement>('button[data-action="refresh-shipping-groups"]').forEach((btn) => {
      btn.addEventListener('click', () => onRefreshShippingGroupsClick(btn, ctx));
    });
    placeholder.querySelectorAll<HTMLSelectElement>('select[data-action="set-default-shipping-group"]').forEach((sel) => {
      sel.addEventListener('change', () => void onDefaultShippingGroupChange(sel, ctx));
    });
  });
}

function renderShippingGroupBlock(storefront: string, row: ShippingGroupStatusRow | undefined): string {
  const count = row?.count ?? 0;
  const sub = row?.lastSyncedAt
    ? `letzter Sync ${formatRelative(row.lastSyncedAt)}`
    : '';

  // Design "Kaufland Einstellungen": .mp-stat + .mp-sync + optionaler Standard-Select + .mp-btn.
  if (count === 0) {
    return `
      <div class="mp-stat">${SVG_TRUCK}Versandgruppen: <b>0</b></div>
      <div class="mp-sync warn"><span class="d"></span>noch nicht geladen <span class="when">· bitte aktualisieren</span></div>
      <button type="button" class="mp-btn" data-action="refresh-shipping-groups" data-storefront="${storefront}">
        <span class="v-icon fa-regular fa-arrows-rotate" data-icon-default></span>
        Versandgruppen Aktualisieren
      </button>
    `;
  }

  const countLabel = `${count} Versandgruppe${count === 1 ? '' : 'n'}`;
  const groups = row?.groups ?? [];
  const defaultId = row?.defaultShippingGroupId ?? '';
  const defaultSelect = groups.length > 0 ? `
    <div class="mp-field">
      <span class="mp-flab">Standard-Versandgruppe</span>
      <div class="mp-select">
        <select id="kl-sg-default-${escapeHtml(storefront)}"
                data-action="set-default-shipping-group"
                data-storefront="${escapeHtml(storefront)}"
                data-previous-value="${escapeHtml(defaultId)}">
          <option value="" ${defaultId === '' ? 'selected' : ''}>— kein Standard —</option>
          ${groups.map((g) => {
            const label = g.name ?? g.id_shipping_group;
            const isSelected = g.id_shipping_group === defaultId ? 'selected' : '';
            return `<option value="${escapeHtml(g.id_shipping_group)}" ${isSelected}>${escapeHtml(label)}</option>`;
          }).join('')}
        </select>
      </div>
    </div>
    <div class="mp-note">Wird im Aufschalten-Lister automatisch vorbelegt.</div>
  ` : '';

  return `
    <div class="mp-stat">${SVG_TRUCK}Versandgruppen: <b>${count}</b></div>
    <div class="mp-sync ok"><span class="d"></span>${SVG_CHECK_SM}${escapeHtml(countLabel)}${sub ? ` <span class="when">· ${escapeHtml(sub)}</span>` : ''}</div>
    ${defaultSelect}
    <button type="button" class="mp-btn" data-action="refresh-shipping-groups" data-storefront="${storefront}">
      <span class="v-icon fa-regular fa-arrows-rotate" data-icon-default></span>
      Versandgruppen Aktualisieren
    </button>
  `;
}

async function onDefaultShippingGroupChange(sel: HTMLSelectElement, ctx: SettingsCtx): Promise<void> {
  const storefront = sel.dataset.storefront!;
  const newDefault = sel.value || null;
  const previousValue = sel.dataset.previousValue ?? '';
  sel.disabled = true;

  // Aktuelles globales Profil holen — wir mergen die Aenderung,
  // damit Save (Upsert) keine anderen Felder auf NULL setzt.
  const getRes = await ctx.supabase.rpc('kaufland_get_offer_default_profile', { _storefront: storefront });
  if (getRes.error) {
    showToast(`Standard konnte nicht geladen werden: ${getRes.error.message}`, 'error');
    sel.value = previousValue;
    sel.disabled = false;
    return;
  }
  const profile = (getRes.data ?? {}) as { global?: Record<string, unknown> | null };
  const current = profile.global ?? {};

  const fields = {
    condition: current['condition'] ?? null,
    handling_time: current['handling_time'] ?? null,
    id_shipping_group: newDefault,
    vat_indicator: current['vat_indicator'] ?? null,
    field_mappings: current['field_mappings'] ?? {},
  };

  const saveRes = await ctx.supabase.rpc('kaufland_save_offer_default_profile', {
    _storefront: storefront,
    _category_id: null,
    _fields: fields,
  });
  if (saveRes.error) {
    showToast(`Standard speichern fehlgeschlagen: ${saveRes.error.message}`, 'error');
    sel.value = previousValue;
    sel.disabled = false;
    return;
  }

  sel.dataset.previousValue = sel.value;
  sel.disabled = false;
  const label = sel.options[sel.selectedIndex]?.text ?? '';
  showToast(newDefault
    ? `Standard auf „${label}" gesetzt.`
    : 'Standard-Versandgruppe entfernt.',
    'success');
}

async function onRefreshShippingGroupsClick(btn: HTMLButtonElement, ctx: SettingsCtx): Promise<void> {
  const storefront = btn.dataset.storefront!;
  btn.disabled = true;
  const icon = btn.querySelector<HTMLElement>('[data-icon-default]');
  if (icon) icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';

  const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_refresh_shipping_groups', {
    _storefront: storefront,
  });
  if (error) {
    btn.disabled = false;
    if (icon) icon.className = 'v-icon fa-regular fa-arrows-rotate';
    showToast(`Konnte Sync nicht starten: ${error.message}`, 'error');
    return;
  }
  const row = Array.isArray(data) ? data[0] : data;
  const jobId = (row as { job_id?: string } | null)?.job_id;
  if (!jobId) {
    btn.disabled = false;
    if (icon) icon.className = 'v-icon fa-regular fa-arrows-rotate';
    showToast('Sync-Job konnte nicht angelegt werden.', 'error');
    return;
  }

  try {
    const result = await awaitJob(ctx.supabase, jobId, 60_000);
    const r = (result?.result ?? {}) as { groupsTotal?: number; errors?: unknown[] };
    const groupsTotal = r.groupsTotal ?? 0;
    const hasErrors = Array.isArray(r.errors) && r.errors.length > 0;
    if (hasErrors) {
      showToast('Sync mit Fehler abgeschlossen — siehe Konsole / Worker-Log.', 'error');
      console.warn('[settings] refresh_shipping_groups errors:', r.errors);
    } else if (groupsTotal === 0) {
      showToast('Sync abgeschlossen — Kaufland hat keine Versandgruppen geliefert. Bitte im Kaufland-Sellerportal mindestens eine anlegen.', 'info');
    } else {
      showToast(`${groupsTotal} Versandgruppe${groupsTotal === 1 ? '' : 'n'} synchronisiert.`, 'success');
    }
  } catch (e) {
    showToast(`Sync fehlgeschlagen: ${(e as Error).message}`, 'error');
  } finally {
    btn.disabled = false;
    if (icon) icon.className = 'v-icon fa-regular fa-arrows-rotate';
  }

  // Frische Daten in den Block einspielen.
  const listEl = btn.closest<HTMLDivElement>('.kl-storefront-grid');
  if (listEl) {
    const rows = await fetchShippingGroupStatus(ctx);
    applyShippingGroupStatus(listEl, rows, ctx);
  }
}

// ----- Row rendering -------------------------------------------------------
// Markup folgt der Design-Reference 2026-05-22: kl-storefront-card mit
// kl-storefront-head (Flag + Title + Status-Pill) + State-spezifischem Body.
// Modifier-Klasse `is-muted` ersetzt das BEM-`--muted`.

function renderRow(c: StorefrontDef, s: RowState): string {
  // Marktplatz-Badge rechts oben (Design "Kaufland Einstellungen"): Punkt + Text.
  // `.kl-storefront-status` bleibt als Hook-Klasse (activateStorefront tauscht ihn).
  let badge = '';
  switch (s.state) {
    case 'active':
      badge = `<span class="mp-badge ok"><span class="d"></span>Aktiv</span>`;
      break;
    case 'channel-missing':
      badge = `<span class="mp-badge warn" title="DB sagt aktiv, aber Vario hat keinen passenden Sales-Channel mehr"><span class="d"></span>In Vario gelöscht</span>`;
      break;
    case 'inactive-available':
      badge = `<span class="mp-badge off"><span class="d"></span>Inaktiv</span>`;
      break;
    case 'not-authorised':
      badge = `<span class="mp-badge off" title="${escapeHtml(s.probeMessage ?? '')}"><span class="d"></span>Nicht freigeschaltet</span>`;
      break;
  }

  let bodyHtml = '';
  switch (s.state) {
    case 'active':
      bodyHtml = renderActiveBody(c, s);
      break;
    case 'channel-missing':
      bodyHtml = renderChannelMissingBody(c, s);
      break;
    case 'inactive-available':
      bodyHtml = renderInactiveBody(c);
      break;
    case 'not-authorised':
      bodyHtml = renderNotAuthBody();
      break;
  }

  const locked = s.state === 'not-authorised' ? ' locked' : '';
  return `
    <article class="mp-card${locked} kl-storefront-card" data-storefront="${c.code}" data-storefront-label="${escapeHtml(c.label)}">
      <div class="mp-head">
        <span class="mp-code">${escapeHtml(c.code.toUpperCase())}</span>
        <span class="mp-name">Kaufland ${escapeHtml(c.label)}</span>
        <span class="kl-storefront-status">${badge}</span>
      </div>
      ${bodyHtml}
    </article>
  `;
}

function renderActiveBody(c: StorefrontDef, s: RowState): string {
  return `
    ${renderStorefrontSettings(c.code, s)}
    <div class="mp-sec border">
      <div class="mp-lab">Kategorien</div>
      <div data-categories-block>Lade Kategorien-Status …</div>
    </div>
    <div class="mp-sec border">
      <div class="mp-lab">Versandgruppen-Sync</div>
      <div data-shipping-groups-block>Lade Versandgruppen-Status …</div>
    </div>
  `;
}

function renderChannelMissingBody(c: StorefrontDef, s: RowState): string {
  // Wir behalten den Settings-Block (Lager etc. sind unabhängig vom Vario-
  // Channel), und ergänzen oben einen Repair-Aufruf — der aus dem Verlust
  // herauskommt, wenn ein Operator den Channel in Vario gelöscht hat.
  return `
    <div class="mp-sec"><div class="mp-action">
      <button type="button" class="mp-btn" data-action="repair" data-storefront="${c.code}">
        <span class="v-icon fa-regular fa-rotate-right" data-icon-default></span>
        Channel neu anlegen
      </button>
      <span class="mp-msg" data-msg></span>
    </div></div>
    ${renderStorefrontSettings(c.code, s)}
  `;
}

function renderInactiveBody(c: StorefrontDef): string {
  return `
    <div class="mp-sec"><div class="mp-action">
      <p class="mp-hint">Für diesen Seller-Account freigeschaltet, aber noch nicht als Sales-Channel in Vario angelegt.</p>
      <button type="button" class="mp-btn" data-action="activate" data-storefront="${c.code}">
        <span class="v-icon fa-regular fa-plus" data-icon-default></span>
        Aktivieren
      </button>
      <span class="mp-msg" data-msg></span>
    </div></div>
  `;
}

function renderNotAuthBody(): string {
  return `
    <div class="mp-locked">
      ${SVG_LOCK}
      <p>Dieser Marktplatz ist für deinen Seller-Account aktuell nicht freigeschaltet.</p>
    </div>
  `;
}

function renderStorefrontSettings(code: string, s: RowState): string {
  const storageId = s.storageId ?? '';
  const storageLabel = s.storageLabel ?? '';
  const paymentMethodId = s.paymentMethodId ?? '';
  const paymentMethodLabel = s.paymentMethodLabel ?? '';
  const paymentTermId = s.paymentTermId ?? '';
  const paymentTermLabel = s.paymentTermLabel ?? '';
  const deliveryMethodId = s.deliveryMethodId ?? '';
  const deliveryMethodLabel = s.deliveryMethodLabel ?? '';
  const deliveryTermId = s.deliveryTermId ?? '';
  const deliveryTermLabel = s.deliveryTermLabel ?? '';
  const suppress = s.suppressStockPush === true;
  const autoOn = s.autoTakeoverOrders === true;
  const fallbackOn = s.orderDefaultChannelFallback === true;
  // Streng auf `true` pruefen: Ein fehlender Wert (aeltere Backend-Version, die die Spalten noch
  // nicht mitliefert) darf nie als „sendet" dargestellt werden.
  const pushInvOn = s.pushInvoices === true;
  const pushShipOn = s.pushShipments === true;
  const storageIds = s.storageIds ?? (storageId ? [storageId] : []);
  const storagePanelHtml = cachedWarehouses
    ? renderWarehousePanel(code, cachedWarehouses, storageIds, storageId || null)
    : '';
  const storageSummary = cachedWarehouses
    ? warehouseSummary(storageIds, storageId || null, labelOfWarehouseId)
    : 'Lade Vario-Lager …';
  const methodOptionsHtml = cachedPaymentMethods
    ? renderPaymentOptions(cachedPaymentMethods, paymentMethodId)
    : `<option value="" disabled selected>Lade Zahlarten …</option>`;
  const termOptionsHtml = cachedPaymentTerms
    ? renderPaymentOptions(cachedPaymentTerms, paymentTermId)
    : `<option value="" disabled selected>Lade Zahlungsbedingungen …</option>`;
  const deliveryMethodOptionsHtml = cachedDeliveryMethods
    ? renderPaymentOptions(cachedDeliveryMethods, deliveryMethodId)
    : `<option value="" disabled selected>Lade Versandarten …</option>`;
  const deliveryTermOptionsHtml = cachedDeliveryTerms
    ? renderPaymentOptions(cachedDeliveryTerms, deliveryTermId)
    : `<option value="" disabled selected>Lade Lieferbedingungen …</option>`;
  // Verstecke Label-Hinweis (escapeHtml) für Storage/Lager-Label.
  // Hidden-input-Data-Attribute reichen für die Backend-Calls.
  return `
    <div data-settings-block>

      <div class="mp-sec">
        <div class="mp-lab">Bestand</div>
        <div class="mp-row">
          <div class="mp-multi" data-storage-multi data-storefront="${code}"
               data-selected-ids="${escapeHtml(storageIds.join(','))}"
               data-default-id="${escapeHtml(storageId)}"
               data-current-label="${escapeHtml(storageLabel)}">
            <button type="button" class="mp-multi-btn" id="kl-storage-${code}"
                    data-storage-toggle aria-expanded="false"
                    title="Vario-Lager auswählen — der gemeldete Bestand ist die Summe der markierten Lager. Das Standard-Lager ist Ziel für übernommene Aufträge.">
              <span data-storage-summary>${escapeHtml(storageSummary)}</span>
            </button>
            <div class="mp-multi-panel" data-storage-panel hidden>${storagePanelHtml}</div>
          </div>
          <button type="button" class="mp-iconbtn"
                  data-action="reload-warehouses" data-storefront="${code}"
                  title="Vario-Lager-Liste neu laden">
            <span class="v-icon fa-regular fa-rotate-right" data-icon-default></span>
          </button>
        </div>
        <div class="mp-msg" data-storage-msg></div>

        <label class="mp-check" title="Stock-Sync-Worker überspringt diese Storefront. Preise und Status laufen weiter.">
          <input type="checkbox" data-suppress-stock data-storefront="${code}" ${suppress ? 'checked' : ''} />
          <span><b>Keine Lagerbestand-Änderungen an Kaufland senden</b><em>Stock-Sync-Worker überspringt diese Storefront. Preise und Status laufen weiter.</em></span>
        </label>
        <div class="mp-msg" data-suppress-msg></div>
      </div>

      <div class="mp-sec border">
        <div class="mp-lab">Auftragsübernahme</div>

        <label class="mp-check"
               title="Bestellungen werden automatisch übernommen + gespeichert, sobald ihr Status auf „zu versenden" wechselt. Bestellungen im Status „offen" bleiben unberührt, bis Kaufland den Versandfreigabe-Wechsel meldet. Folgebelege (Lieferschein, Rechnung) legt der Händler in Vario manuell an — sie werden über Webhooks zurücksynchronisiert.">
          <input type="checkbox" data-auto-takeover data-storefront="${code}" ${autoOn ? 'checked' : ''} />
          <span><b>Bestellungen automatisch übernehmen</b><em>Übernahme + Speichern startet automatisch, sobald Kaufland den Status auf „zu versenden" setzt.</em></span>
        </label>
        <div class="mp-msg" data-auto-takeover-msg></div>

        <label class="mp-check"
               title="Für Händler, die die App nur für den Auftragsimport nutzen und ihre Angebote nicht einzeln zuordnen. Ist eine Position keinem Vario-Artikel zugeordnet, sucht die Übernahme den Artikel über die EAN im Vario-Standard-Verkaufskanal. Genau ein Treffer wird in den Auftrag übernommen. Kein oder mehrere Treffer: die Übernahme bricht ab und nennt die EAN. Es entsteht dabei keine Zuordnung, Bestand und Preis werden für diesen Artikel nicht an Kaufland gesendet.">
          <input type="checkbox" data-default-channel-fallback data-storefront="${code}" ${fallbackOn ? 'checked' : ''} />
          <span><b>Nicht zugeordnete Artikel im Standard-Verkaufskanal suchen</b><em>Nur für den Auftrag: Suche über die EAN, genau ein Treffer wird übernommen. Keine Zuordnung, kein Bestands- oder Preisabgleich.</em></span>
        </label>
        <div class="mp-msg" data-default-channel-fallback-msg></div>

        <label class="mp-check"
               title="Ist die Option aus, wird das Rechnungs-PDF in Vario trotzdem erzeugt und im DMS abgelegt — es wird nur nicht an Kaufland gesendet. Eingeschaltet landet die Rechnung beim Käufer.">
          <input type="checkbox" data-push-invoices data-storefront="${code}" ${pushInvOn ? 'checked' : ''} />
          <span><b>Rechnungen an Kaufland senden</b><em>Aus: Beleg wird in Vario erzeugt und im DMS abgelegt, aber nicht übertragen. Ein: die Rechnung geht an den Käufer.</em></span>
        </label>
        <div class="mp-msg" data-push-invoices-msg></div>

        <label class="mp-check"
               title="Betrifft die Versandmeldung mit Trackingnummer. Eingeschaltet setzt Kaufland die Bestellung auf versendet und benachrichtigt den Käufer.">
          <input type="checkbox" data-push-shipments data-storefront="${code}" ${pushShipOn ? 'checked' : ''} />
          <span><b>Versandmeldungen an Kaufland senden</b><em>Aus: „Versand anlegen" bricht mit einem Hinweis ab. Ein: Kaufland setzt die Bestellung auf versendet und benachrichtigt den Käufer.</em></span>
        </label>
        <div class="mp-msg" data-push-shipments-msg></div>

        <div class="mp-field">
          <span class="mp-flab">Zahlart</span>
          <div class="mp-select">
            <select id="kl-payment-method-${code}"
                    data-payment-method-select data-storefront="${code}"
                    data-current-id="${escapeHtml(paymentMethodId)}"
                    data-current-label="${escapeHtml(paymentMethodLabel)}"
                    title="Vario-Zahlart für übernommene Aufträge">
              ${methodOptionsHtml}
            </select>
          </div>
          <div class="mp-msg" data-payment-method-msg></div>
        </div>

        <div class="mp-field">
          <span class="mp-flab">Zahlungsbedingung</span>
          <div class="mp-select">
            <select id="kl-payment-term-${code}"
                    data-payment-term-select data-storefront="${code}"
                    data-current-id="${escapeHtml(paymentTermId)}"
                    data-current-label="${escapeHtml(paymentTermLabel)}"
                    title="Vario-Zahlungsbedingung für übernommene Aufträge">
              ${termOptionsHtml}
            </select>
          </div>
          <div class="mp-msg" data-payment-term-msg></div>
        </div>

        <div class="mp-field">
          <span class="mp-flab">Standard Versandart</span>
          <div class="mp-select">
            <select id="kl-delivery-method-${code}"
                    data-delivery-method-select data-storefront="${code}"
                    data-current-id="${escapeHtml(deliveryMethodId)}"
                    data-current-label="${escapeHtml(deliveryMethodLabel)}"
                    title="Vario-Versandart für übernommene Aufträge">
              ${deliveryMethodOptionsHtml}
            </select>
          </div>
          <div class="mp-msg" data-delivery-method-msg></div>
        </div>

        <div class="mp-field">
          <span class="mp-flab">Lieferbedingung</span>
          <div class="mp-select">
            <select id="kl-delivery-term-${code}"
                    data-delivery-term-select data-storefront="${code}"
                    data-current-id="${escapeHtml(deliveryTermId)}"
                    data-current-label="${escapeHtml(deliveryTermLabel)}"
                    title="Vario-Lieferbedingung für übernommene Aufträge">
              ${deliveryTermOptionsHtml}
            </select>
          </div>
          <div class="mp-msg" data-delivery-term-msg></div>
        </div>
      </div>
    </div>
  `;
}

/** Ein Lager-Label für die Anzeige (DisplayName → Nummer → ID). */
function warehouseLabel(w: VarioWarehouse): string {
  return w.displayName ?? w.number ?? w.id;
}

/** Label-Auflösung über die zuletzt geladene Lagerliste; unbekannte ID → ID selbst. */
function labelOfWarehouseId(id: string): string {
  const hit = cachedWarehouses?.find((w) => w.id === id);
  return hit ? warehouseLabel(hit) : id;
}

/** Panel-Inhalt: je Lager eine Checkbox (Auswahl) + ein Radio (Standard). Nicht markierte
 *  Lager können nicht Standard sein → ihr Radio ist disabled. */
function renderWarehousePanel(
  code: string,
  warehouses: VarioWarehouse[],
  selectedIds: string[],
  defaultId: string | null,
): string {
  if (warehouses.length === 0) {
    return `<span class="mp-multi-empty">Keine Vario-Lager gefunden — bitte in Vario anlegen.</span>`;
  }
  const sel = new Set(selectedIds);
  const def = resolveDefaultSelection(selectedIds, defaultId);
  return warehouses
    .map((w) => {
      const label = warehouseLabel(w);
      const checked = sel.has(w.id);
      return `<div class="mp-multi-opt">
        <label class="mp-multi-pick">
          <input type="checkbox" data-storage-cb value="${escapeHtml(w.id)}" data-label="${escapeHtml(label)}"${checked ? ' checked' : ''} />
          <span class="mp-multi-name">${escapeHtml(label)}${w.salesStorage ? `<span class="mp-multi-tag">· Verkauf</span>` : ''}</span>
        </label>
        <label class="mp-multi-def" title="Standard-Lager: Ziel für übernommene Aufträge und Varios defaultSalesStorage">
          <input type="radio" data-storage-radio name="kl-wh-def-${escapeHtml(code)}" value="${escapeHtml(w.id)}"${w.id === def ? ' checked' : ''}${checked ? '' : ' disabled'} />Standard
        </label>
      </div>`;
    })
    .join('');
}

/** Aktueller Stand AUS DEN INPUTS (nicht der gespeicherte). */
function readMultiState(wrap: HTMLElement): { ids: string[]; defaultId: string | null; defaultLabel: string | null } {
  const ids = Array.from(wrap.querySelectorAll<HTMLInputElement>('input[data-storage-cb]'))
    .filter((cb) => cb.checked)
    .map((cb) => cb.value);
  const radio = wrap.querySelector<HTMLInputElement>('input[data-storage-radio]:checked');
  const defaultId = resolveDefaultSelection(ids, radio?.value ?? null);
  const defaultCb = defaultId
    ? wrap.querySelector<HTMLInputElement>(`input[data-storage-cb][value="${CSS.escape(defaultId)}"]`)
    : null;
  return { ids, defaultId, defaultLabel: defaultCb?.getAttribute('data-label') ?? null };
}

/** Radios an die Checkboxen angleichen (nicht markiert → nicht wählbar) und die
 *  Zusammenfassung im Button nachziehen. */
function syncMultiUi(wrap: HTMLElement): void {
  const { ids, defaultId } = readMultiState(wrap);
  wrap.querySelectorAll<HTMLInputElement>('input[data-storage-radio]').forEach((radio) => {
    const cb = wrap.querySelector<HTMLInputElement>(`input[data-storage-cb][value="${CSS.escape(radio.value)}"]`);
    const on = !!cb?.checked;
    radio.disabled = !on;
    if (!on) radio.checked = false;
  });
  if (defaultId) {
    const target = wrap.querySelector<HTMLInputElement>(`input[data-storage-radio][value="${CSS.escape(defaultId)}"]`);
    if (target && !target.checked) target.checked = true;
  }
  const summaryEl = wrap.querySelector<HTMLElement>('[data-storage-summary]');
  if (summaryEl) summaryEl.textContent = warehouseSummary(ids, defaultId, labelOfWarehouseId);
}

/** Panel einer Karte (neu) rendern — nach dem Laden/Neuladen der Lagerliste. Nimmt als
 *  Ausgangsauswahl den GESPEICHERTEN Stand aus dem dataset, nicht die alten Inputs. */
function applyWarehousesToMulti(wrap: HTMLElement, warehouses: VarioWarehouse[]): void {
  const code = wrap.dataset.storefront ?? '';
  const savedIds = (wrap.dataset.selectedIds ?? '').split(',').filter((s) => s.length > 0);
  const savedDefault = wrap.dataset.defaultId ?? null;
  const panel = wrap.querySelector<HTMLElement>('[data-storage-panel]');
  if (!panel) return;
  panel.innerHTML = renderWarehousePanel(code, warehouses, savedIds, savedDefault || null);
  bindMultiInputs(wrap);
  syncMultiUi(wrap);
}

/** Checkbox-/Radio-Listener nach jedem Panel-Render neu setzen (innerHTML ersetzt die Nodes). */
function bindMultiInputs(wrap: HTMLElement): void {
  wrap.querySelectorAll<HTMLInputElement>('input[data-storage-cb], input[data-storage-radio]')
    .forEach((inp) => inp.addEventListener('change', () => syncMultiUi(wrap)));
}

function renderPaymentOptions(opts: PaymentOption[], currentId: string): string {
  if (opts.length === 0) {
    return `<option value="" disabled selected>Keine Einträge gefunden</option>`;
  }
  const out = [`<option value="">— bitte auswählen —</option>`];
  for (const o of opts) {
    const selected = o.id === currentId ? ' selected' : '';
    out.push(`<option value="${escapeHtml(o.id)}"${selected}>${escapeHtml(o.label)}</option>`);
  }
  return out.join('');
}

function applyPaymentOptionsToSelect(
  sel: HTMLSelectElement,
  opts: PaymentOption[],
  currentId: string,
): void {
  sel.innerHTML = renderPaymentOptions(opts, currentId);
}

async function loadVarioPaymentOptions(
  ctx: SettingsCtx,
): Promise<{
  methods: PaymentOption[];
  terms: PaymentOption[];
  deliveryMethods: PaymentOption[];
  deliveryTerms: PaymentOption[];
}> {
  const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-payment-options-list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
    body: '{}',
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`vario-payment-options-list ${res.status}: ${body.slice(0, 300)}`);
  }
  const body = (await res.json()) as {
    ok?: boolean;
    methods?: PaymentOption[];
    terms?: PaymentOption[];
    deliveryMethods?: PaymentOption[];
    deliveryTerms?: PaymentOption[];
    error?: { message: string };
  };
  if (!body.ok) {
    throw new Error(body.error?.message ?? 'vario-payment-options-list: unbekannter Fehler');
  }
  return {
    methods: body.methods ?? [],
    terms: body.terms ?? [],
    deliveryMethods: body.deliveryMethods ?? [],
    deliveryTerms: body.deliveryTerms ?? [],
  };
}

async function savePaymentMethod(sel: HTMLSelectElement, ctx: SettingsCtx): Promise<void> {
  const storefront = sel.dataset.storefront!;
  const card = sel.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-payment-method-msg]');
  const id = sel.value.trim();
  const label = sel.selectedOptions?.[0]?.textContent?.trim() ?? '';
  if (!id) {
    if (msgEl) {
      msgEl.textContent = 'Bitte eine Zahlart auswählen.';
      msgEl.className = 'mp-msg err';
    }
    return;
  }

  sel.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_payment_method', {
    _storefront: storefront,
    _method_id: id,
    _method_label: label || null,
  });
  sel.disabled = false;
  if (msgEl) {
    if (error) {
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      sel.dataset['currentId'] = id;
      msgEl.textContent = '';
      msgEl.className = 'mp-msg';
    }
  }
}

async function savePaymentTerm(sel: HTMLSelectElement, ctx: SettingsCtx): Promise<void> {
  const storefront = sel.dataset.storefront!;
  const card = sel.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-payment-term-msg]');
  const id = sel.value.trim();
  const label = sel.selectedOptions?.[0]?.textContent?.trim() ?? '';
  if (!id) {
    if (msgEl) {
      msgEl.textContent = 'Bitte eine Zahlungsbedingung auswählen.';
      msgEl.className = 'mp-msg err';
    }
    return;
  }
  sel.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_payment_term', {
    _storefront: storefront,
    _term_id: id,
    _term_label: label || null,
  });
  sel.disabled = false;
  if (msgEl) {
    if (error) {
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      msgEl.textContent = 'Zahlungsbedingung gespeichert.';
      msgEl.className = 'mp-msg ok';
    }
  }
}

async function saveDeliveryMethod(sel: HTMLSelectElement, ctx: SettingsCtx): Promise<void> {
  const storefront = sel.dataset.storefront!;
  const card = sel.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-delivery-method-msg]');
  const id = sel.value.trim();
  const label = sel.selectedOptions?.[0]?.textContent?.trim() ?? '';
  if (!id) {
    if (msgEl) {
      msgEl.textContent = 'Bitte eine Versandart auswählen.';
      msgEl.className = 'mp-msg err';
    }
    return;
  }
  sel.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_delivery_method', {
    _storefront: storefront,
    _method_id: id,
    _method_label: label || null,
  });
  sel.disabled = false;
  if (msgEl) {
    if (error) {
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      msgEl.textContent = 'Versandart gespeichert.';
      msgEl.className = 'mp-msg ok';
    }
  }
}

async function saveDeliveryTerm(sel: HTMLSelectElement, ctx: SettingsCtx): Promise<void> {
  const storefront = sel.dataset.storefront!;
  const card = sel.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-delivery-term-msg]');
  const id = sel.value.trim();
  const label = sel.selectedOptions?.[0]?.textContent?.trim() ?? '';
  if (!id) {
    if (msgEl) {
      msgEl.textContent = 'Bitte eine Lieferbedingung auswählen.';
      msgEl.className = 'mp-msg err';
    }
    return;
  }
  sel.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_delivery_term', {
    _storefront: storefront,
    _term_id: id,
    _term_label: label || null,
  });
  sel.disabled = false;
  if (msgEl) {
    if (error) {
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      msgEl.textContent = 'Lieferbedingung gespeichert.';
      msgEl.className = 'mp-msg ok';
    }
  }
}

async function loadVarioWarehouses(ctx: SettingsCtx): Promise<VarioWarehouse[]> {
  const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-warehouses-list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
    body: '{}',
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`vario-warehouses-list ${res.status}: ${body.slice(0, 300)}`);
  }
  const body = (await res.json()) as { ok?: boolean; warehouses?: VarioWarehouse[]; error?: { message: string } };
  if (!body.ok || !body.warehouses) {
    throw new Error(body.error?.message ?? 'vario-warehouses-list: unbekannter Fehler');
  }
  return body.warehouses;
}

/**
 * Aktiviert eine Storefront (oder legt bei `repair` den Vario-Channel neu an) und rendert den
 * Tab danach über `onActivated` komplett neu. Der Re-Render ist der Kern: eine frisch aktivierte
 * Karte braucht `renderActiveBody` (Einstellungs-Block + Kategorien-/Versandgruppen-Sync) und
 * darf den „Aktivieren"-Button nicht mehr zeigen — beides lässt sich nicht sinnvoll in die
 * bestehende `inactive`-Karte hineinflicken.
 */
async function activateStorefront(btn: HTMLButtonElement, ctx: SettingsCtx, onActivated: () => void) {
  const storefront = btn.dataset.storefront!;
  const card = btn.closest<HTMLElement>('.kl-storefront-card')!;
  const msgEl = card.querySelector<HTMLSpanElement>('[data-msg]');
  const icon = btn.querySelector<HTMLElement>('[data-icon-default]');
  const originalIconClass = icon?.className ?? 'v-icon fa-regular fa-plus';

  btn.disabled = true;
  if (icon) icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';
  if (msgEl) {
    msgEl.textContent = btn.dataset.action === 'repair' ? 'Lege neuen Channel an …' : 'Aktiviere …';
    msgEl.className = 'mp-msg info';
  }

  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/kaufland-storefront-add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
      body: JSON.stringify({ storefront }),
    });
    const payload = (await res.json()) as StorefrontAddResponse;
    if (!res.ok || !payload.ok) {
      const probe = payload.error?.probe;
      const probeNote = probe && !probe.ok ? ` (${probe.status ?? ''} ${probe.message ?? ''})`.trim() : '';
      const message = `${payload.error?.message ?? `HTTP ${res.status}`}${probeNote}`;
      if (msgEl) {
        msgEl.textContent = message;
        msgEl.className = 'mp-msg err';
      }
      btn.disabled = false;
      if (icon) icon.className = originalIconClass;
      return;
    }

    // Sofort-Feedback am Badge, damit die Karte nicht „unverändert" wirkt, während der
    // Re-Render die Freischaltungen neu prüft (der zeigt kurz „Prüfe Freischaltungen …").
    const statusEl = card.querySelector<HTMLElement>('.kl-storefront-status');
    if (statusEl) {
      statusEl.innerHTML = `<span class="mp-badge ok"><span class="d"></span>Aktiv</span>`;
    }
    if (msgEl) {
      msgEl.textContent = payload.alreadyPresent ? 'War bereits aktiv.' : 'Aktiviert.';
      msgEl.className = 'mp-msg ok';
    }
    showToast(
      payload.alreadyPresent
        ? `${payload.label ?? storefront.toUpperCase()} war bereits aktiv.`
        : `${payload.label ?? storefront.toUpperCase()} aktiviert.`,
      'success',
    );

    // Kompletter Re-Render: erst danach hat die Karte ihren Einstellungs-Block
    // (Vario-Lager, Checkboxen, Zahlart/Zahlungsbedingung/Versandart/Lieferbedingung)
    // sowie die Kategorien-/Versandgruppen-Blöcke — und den „Aktivieren"-Button nicht mehr.
    onActivated();
  } catch (e) {
    if (msgEl) {
      msgEl.textContent = `Fehler: ${(e as Error).message}`;
      msgEl.className = 'mp-msg err';
    }
    btn.disabled = false;
    if (icon) icon.className = originalIconClass;
  }
}

/** Panel öffnen. Andere offene Panels schließen (und dabei speichern, falls geändert). */
function openStoragePanel(wrap: HTMLElement, ctx: SettingsCtx): void {
  document.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((other) => {
    if (other !== wrap) void closeStoragePanel(other, ctx);
  });
  const panel = wrap.querySelector<HTMLElement>('[data-storage-panel]');
  const btn = wrap.querySelector<HTMLButtonElement>('[data-storage-toggle]');
  if (!panel || !btn) return;
  panel.hidden = false;
  btn.setAttribute('aria-expanded', 'true');
}

/** Panel schließen und speichern, wenn die Auswahl vom gespeicherten Stand abweicht. */
async function closeStoragePanel(wrap: HTMLElement, ctx: SettingsCtx): Promise<void> {
  const panel = wrap.querySelector<HTMLElement>('[data-storage-panel]');
  const btn = wrap.querySelector<HTMLButtonElement>('[data-storage-toggle]');
  if (!panel || panel.hidden) return;
  panel.hidden = true;
  btn?.setAttribute('aria-expanded', 'false');

  // Solange keine Checkbox gerendert ist (Lagerliste noch nicht geladen oder Abruf
  // fehlgeschlagen), gibt es gar keine Nutzer-Auswahl. Ohne diesen Guard wuerde ein
  // Klick auf das leere Panel die gespeicherte Zuordnung mit [] ueberschreiben.
  if (!wrap.querySelector('input[data-storage-cb]')) return;

  const { ids, defaultId, defaultLabel } = readMultiState(wrap);
  const savedIds = (wrap.dataset.selectedIds ?? '').split(',').filter((s) => s.length > 0);
  const savedDefault = wrap.dataset.defaultId ?? '';
  const changed =
    ids.length !== savedIds.length ||
    ids.some((id) => !savedIds.includes(id)) ||
    (defaultId ?? '') !== savedDefault;
  if (!changed) return;
  await saveStorefrontStorages(wrap, ctx, ids, defaultId, defaultLabel);
}

/** Schreibt Mehrfachauswahl + Standard-Lager über `kaufland_set_storefront_warehouses`
 *  (PLURAL — der alte Singular kann nur ein Lager und existiert nur noch als Delegator
 *  für Bundles vor diesem Deploy).
 *  Leere Auswahl ist erlaubt und leert die Zuordnung — die Vario-Kanal-Zuordnung bleibt
 *  bestehen (das Modell ist additiv). */
async function saveStorefrontStorages(
  wrap: HTMLElement,
  ctx: SettingsCtx,
  ids: string[],
  defaultId: string | null,
  defaultLabel: string | null,
): Promise<void> {
  const storefront = wrap.dataset.storefront!;
  const card = wrap.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-storage-msg]');
  const btn = wrap.querySelector<HTMLButtonElement>('[data-storage-toggle]');

  if (btn) btn.disabled = true;
  if (msgEl) {
    msgEl.textContent = ids.length > 0
      ? 'Speichere und ordne in Vario zu …'
      : 'Lagerauswahl wird geleert …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_warehouses', {
    _storefront: storefront,
    _storage_ids: ids,
    _default_storage_id: defaultId,
    _default_label: defaultLabel,
  });

  if (btn) btn.disabled = false;
  if (msgEl) {
    if (error) {
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else if (ids.length === 0) {
      msgEl.textContent = 'Lagerauswahl geleert. Die Zuordnung am Vario-Kanal bleibt bestehen.';
      msgEl.className = 'mp-msg ok';
    } else {
      msgEl.textContent = `${ids.length} Lager gespeichert. Zuordnung in Vario läuft im Hintergrund.`;
      msgEl.className = 'mp-msg ok';
    }
  }

  // Gespeicherten Stand im dataset nachfuehren, sonst gilt beim naechsten Schliessen
  // alles erneut als geaendert.
  if (!error) {
    wrap.dataset['selectedIds'] = ids.join(',');
    wrap.dataset['defaultId'] = defaultId ?? '';
  }
}

/**
 * Persistiert den Kanal-Abgleich: Lager, die dem Vario-Verkaufskanal nicht mehr zugeordnet sind,
 * fliegen aus der gespeicherten Auswahl. Vario ist bei der Zuordnung führend.
 *
 * Läuft beim Öffnen der Einstellungen, nicht im Hintergrund — die Anzeige zeigt den gekürzten
 * Stand also sofort, die DB folgt Millisekunden später. Der Händler bekommt eine Meldung, weil
 * eine stillschweigend geänderte Bestandsquelle die Menge bei Kaufland verändert.
 *
 * Bewusst KEIN Aufruf, wenn nichts wegfiel: die Funktion wird nur für Storefronts mit
 * `removed.length > 0` gerufen, sonst gäbe es bei jedem Öffnen einen Schreibvorgang plus einen
 * `vario.ensure_channel_storages`-Job.
 */
async function persistChannelReconcile(
  listEl: HTMLElement,
  ctx: SettingsCtx,
  storefront: string,
  sync: { ids: string[]; defaultId: string | null; removed: string[] },
): Promise<void> {
  const card = listEl.querySelector<HTMLElement>(`.kl-storefront-card[data-storefront="${CSS.escape(storefront)}"]`);
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-storage-msg]');
  const namen = sync.removed.map((id) => labelOfWarehouseId(id)).join(', ');

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_warehouses', {
    _storefront: storefront,
    _storage_ids: sync.ids,
    _default_storage_id: sync.defaultId,
    _default_label: sync.defaultId ? labelOfWarehouseId(sync.defaultId) : null,
  });

  if (!msgEl) return;
  if (error) {
    // Anzeige ist schon gekürzt, die DB nicht — das muss der Händler wissen, sonst hält er den
    // gekürzten Stand für gespeichert und wundert sich beim nächsten Öffnen.
    msgEl.textContent = `In Vario ist „${namen}" dem Kanal nicht mehr zugeordnet. Das Nachziehen der Auswahl ist fehlgeschlagen: ${error.message}`;
    msgEl.className = 'mp-msg err';
    return;
  }
  const wrap = card?.querySelector<HTMLElement>('[data-storage-multi]');
  if (wrap) {
    wrap.dataset['selectedIds'] = sync.ids.join(',');
    wrap.dataset['defaultId'] = sync.defaultId ?? '';
  }
  msgEl.textContent = sync.ids.length > 0
    ? `„${namen}" ist in Vario dem Verkaufskanal nicht mehr zugeordnet und wurde aus der Bestandsquelle entfernt.`
    : `„${namen}" ist in Vario dem Verkaufskanal nicht mehr zugeordnet. Es ist jetzt KEIN Lager gewählt — der Bestand kommt bis zur nächsten Auswahl aus allen Verkaufslagern zusammen.`;
  msgEl.className = sync.ids.length > 0 ? 'mp-msg info' : 'mp-msg err';
}

/** Icon-Button neben der Lager-Auswahl: Vario-Lager-Liste neu vom Edge-Endpoint holen
 *  und alle Lager-Panels (über alle Karten) neu rendern. */
async function reloadWarehouses(btn: HTMLButtonElement, ctx: SettingsCtx): Promise<void> {
  const card = btn.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-storage-msg]');
  const icon = btn.querySelector<HTMLElement>('[data-icon-default]');
  const originalIconClass = icon?.className ?? 'v-icon fa-regular fa-rotate-right';
  btn.disabled = true;
  if (icon) icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';
  try {
    // Der Button liegt AUSSERHALB von .mp-multi: derselbe Klick startet per Bubbling auch
    // den Dokument-Handler, der alle offenen Panels schliesst und dabei speichert. Loest
    // der Lager-Abruf zuerst auf, rendert applyWarehousesToMulti das Panel aus dem noch
    // alten dataset.selectedIds (das erst nach RPC-Erfolg nachgefuehrt wird) — die Summary
    // springt dann sichtbar auf den Vorzustand, obwohl daneben "N Lager gespeichert" steht.
    // Deshalb ALLE Panels abwarten, nicht nur das der eigenen Karte: der Dokument-Handler
    // schliesst ebenfalls alle. Fuer bereits geschlossene Panels ist closeStoragePanel ein
    // No-op (Fruehausstieg auf panel.hidden) → keine unnoetigen Speicher-Calls.
    await Promise.all(
      Array.from(document.querySelectorAll<HTMLElement>('[data-storage-multi]'))
        .map((w) => closeStoragePanel(w, ctx)),
    );
    const warehouses = await loadVarioWarehouses(ctx);
    cachedWarehouses = warehouses;
    const listEl = btn.closest<HTMLElement>('.kl-storefront-grid') ?? document;
    listEl.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((wrap) => {
      applyWarehousesToMulti(wrap, warehouses);
    });
    showToast(
      warehouses.length > 0 ? `${warehouses.length} Vario-Lager geladen.` : 'Keine Vario-Lager gefunden.',
      warehouses.length > 0 ? 'success' : 'info',
    );
    if (msgEl) { msgEl.textContent = ''; msgEl.className = 'mp-msg'; }
  } catch (e) {
    showToast(`Vario-Lager konnten nicht geladen werden: ${(e as Error).message}`, 'error');
  } finally {
    btn.disabled = false;
    if (icon) icon.className = originalIconClass;
  }
}

/** Klick außerhalb und Escape schließen offene Lager-Panels (und speichern dabei).
 *  Nur EINMAL binden — renderSettingsTab läuft bei jedem Refresh erneut, sonst stapeln
 *  sich die Listener und ein Klick löst N Speichervorgänge aus. */
let storageGlobalHandlersBound = false;
function bindStorageGlobalHandlers(ctx: SettingsCtx): void {
  if (storageGlobalHandlersBound) return;
  storageGlobalHandlersBound = true;
  document.addEventListener('click', (ev) => {
    const target = ev.target as Node | null;
    document.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((wrap) => {
      if (target && wrap.contains(target)) return;
      void closeStoragePanel(wrap, ctx);
    });
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape') return;
    document.querySelectorAll<HTMLElement>('[data-storage-multi]').forEach((wrap) => {
      void closeStoragePanel(wrap, ctx);
    });
  });
}

async function toggleSuppressStock(cb: HTMLInputElement, ctx: SettingsCtx): Promise<void> {
  const storefront = cb.dataset.storefront!;
  const card = cb.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-suppress-msg]');
  const wantedValue = cb.checked;
  cb.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_suppress_stock_push', {
    _storefront: storefront,
    _suppress: wantedValue,
  });
  cb.disabled = false;
  if (msgEl) {
    if (error) {
      cb.checked = !wantedValue;
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      msgEl.textContent = wantedValue
        ? 'Bestand-Push deaktiviert. Preis-/Status-Updates laufen weiter.'
        : 'Bestand-Push wieder aktiviert.';
      msgEl.className = 'mp-msg ok';
    }
  }
}

/**
 * Die beiden Kaufland-Versand-Schalter („Rechnungen senden" / „Versandmeldungen senden").
 *
 * Beide folgen dem Muster von `toggleAutoTakeover`: Checkbox sperren, RPC rufen, bei Fehler den
 * sichtbaren Zustand zuruecksetzen. Das Zuruecksetzen ist hier besonders wichtig — eine Checkbox,
 * die „ein" zeigt, obwohl das Speichern fehlschlug, behauptet, Rechnungen gingen raus.
 *
 * Ein gemeinsamer Handler statt zweier Kopien: Es unterscheiden sich nur RPC-Name, Selektor und
 * Meldungstext. Die Texte sagen bewusst, was mit dem Vario-Teil passiert — dass der Beleg auch
 * im „aus"-Fall entsteht und im DMS landet, ist der haeufigste Rueckfrage-Punkt.
 */
async function togglePushFlag(
  cb: HTMLInputElement,
  ctx: SettingsCtx,
  kind: 'invoices' | 'shipments',
): Promise<void> {
  const storefront = cb.dataset.storefront!;
  const card = cb.closest<HTMLElement>('.kl-storefront-card');
  const msgSel = kind === 'invoices' ? '[data-push-invoices-msg]' : '[data-push-shipments-msg]';
  const msgEl = card?.querySelector<HTMLElement>(msgSel);
  const rpc = kind === 'invoices'
    ? 'kaufland_set_storefront_push_invoices'
    : 'kaufland_set_storefront_push_shipments';
  const wanted = cb.checked;

  cb.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc(rpc, { _storefront: storefront, _enabled: wanted });
  cb.disabled = false;
  if (!msgEl) return;

  if (error) {
    cb.checked = !wanted;
    msgEl.textContent = `Fehler: ${error.message}`;
    msgEl.className = 'mp-msg err';
    return;
  }

  if (kind === 'invoices') {
    msgEl.textContent = wanted
      ? 'Rechnungen werden ab jetzt an Kaufland gesendet und landen beim Käufer.'
      : 'Rechnungen werden nicht gesendet. Der Beleg entsteht weiter in Vario und liegt im DMS.';
  } else {
    msgEl.textContent = wanted
      ? 'Versandmeldungen gehen ab jetzt an Kaufland. Kaufland benachrichtigt den Käufer.'
      : 'Versandmeldungen werden nicht gesendet. „Versand anlegen" bricht mit einem Hinweis ab.';
  }
  msgEl.className = 'mp-msg ok';
}

async function toggleAutoTakeover(cb: HTMLInputElement, ctx: SettingsCtx): Promise<void> {
  const storefront = cb.dataset.storefront!;
  const card = cb.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLSpanElement>('[data-auto-takeover-msg]');
  const wantedValue = cb.checked;
  cb.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_auto_takeover_orders', {
    _storefront: storefront,
    _enabled: wantedValue,
  });
  cb.disabled = false;
  if (msgEl) {
    if (error) {
      cb.checked = !wantedValue;
      msgEl.textContent = `Fehler: ${error.message}`;
      msgEl.className = 'mp-msg err';
    } else {
      msgEl.textContent = wantedValue
        ? 'Auto-Übernahme aktiviert. Neue Bestellungen werden ab dem nächsten Refresh automatisch angelegt.'
        : 'Auto-Übernahme deaktiviert. Bestellungen müssen manuell übernommen werden.';
      msgEl.className = 'mp-msg ok';
    }
  }

}

/**
 * Rueckfall Standard-Verkaufskanal (Migration 0067). Gleiches Muster wie `toggleAutoTakeover`:
 * sperren, RPC, bei Fehler den sichtbaren Zustand zuruecksetzen.
 */
async function toggleDefaultChannelFallback(cb: HTMLInputElement, ctx: SettingsCtx): Promise<void> {
  const storefront = cb.dataset.storefront!;
  const card = cb.closest<HTMLElement>('.kl-storefront-card');
  const msgEl = card?.querySelector<HTMLElement>('[data-default-channel-fallback-msg]');
  const wanted = cb.checked;
  cb.disabled = true;
  if (msgEl) {
    msgEl.textContent = 'Speichere …';
    msgEl.className = 'mp-msg info';
  }

  const { error } = await ctx.supabase.rpc('kaufland_set_storefront_order_default_channel_fallback', {
    _storefront: storefront,
    _enabled: wanted,
  });
  cb.disabled = false;
  if (!msgEl) return;

  if (error) {
    cb.checked = !wanted;
    msgEl.textContent = `Fehler: ${error.message}`;
    msgEl.className = 'mp-msg err';
    return;
  }
  msgEl.textContent = wanted
    ? 'Aktiv. Bestellungen mit nicht zugeordneten Positionen lassen sich übernehmen, wenn die EAN im Standard-Verkaufskanal genau einen Artikel trifft.'
    : 'Aus. Bestellungen lassen sich nur übernehmen, wenn alle Positionen zugeordnet sind.';
  msgEl.className = 'mp-msg ok';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}
